import Vue from 'vue'
import fb from '../../firebase'
import helpers from '../../helpers'
import { EventsState, UnsubscribeFunction, RootState, ActionPalleteTabs, WATERMARK_DEFAULT, FirestoreDocSnapper, FirestoreSnapperType, FirestoreQuerySnapper } from '../types'
import { UserProfile } from '@/types/users'
import { AudioMixType, Cast, PlaylistStatus, CastInputStream, CastSummary, InvitedCaster, CallTriage, SupportLevel } from '@/types/casts'
import { Team } from '@/types/teams'
import { SourceStream, Stream, StreamDoc } from '@/types/streams'
import { Asset, AssetType, AssetGroup, SocialMediaTypes } from '@/types/assets'
import { contentSortByOrder, SceneContent } from '@/types/sceneContent'
import { Scene } from '@/types/scenes'
import { ActionContext } from 'vuex'
import store, { moduleActionContext, storehelper } from '@/store'
import { lazyUpdate } from '@/store/storehelper'
import { cloneDeep, reduce, sortBy, isEqual, merge, isEmpty, pickBy } from 'lodash'
import { Widget } from '@/types/widgets'
import { StreamType } from '@/types/streams'
import { KeyDict, TalkbackMode, DeepPartial, CollectionNames, AdminDocNames } from '@/types'
import { VideoPlayer } from '@/types/videoplayer'
import { CloudCastEvent } from '@/types/events'
import { CastChatMessage } from '@/types/casts'
import { difference } from '@/helpers/difference'
import { firestore } from 'firebase/app'
import router from '@/router'
import { CasterCSS, ChatCSS, InstagramCSS, Layout, LayoutBox, LiveChatCSS, SimplecgCSS, TweetCSS } from '@/types/layouts'
import Convertable from '@/classes/Convertable'
import { MVar } from '@/helpers/async_helpers'
import { AudioPayload } from '@/types/audio'
import logsModule from './logs'
import moment from 'moment'
import { Feature, hasFeature } from '@/featureFlags/featureFlags'

const onlineUsers = ['Adam Reinhardt', 'Ryan Mahoney', 'Francis Zane']

let firstEventsChecked = false

type EventsContext = ActionContext<EventsState, RootState>

const createCompletedEventsQuery = async (teamId: string) => {
  return fb.db
    .collection('events')
    .where('team_id', '==', teamId)
    .where('end_date', '<', new Date())
    .where('deleted', '==', false)
}

const maxOrderOfType = (cast: Cast, type: AssetType): number => {
  let order = 0
  Object.values(cast.scenes).forEach(scene => {
    if (scene.contents === undefined) return
    Object.values(scene.contents).forEach(content => {
      if (content.type === type && content.order > order) order = content.order
    })
  })
  return order
}

const isAssetInCast = (cast: Cast, assetId: string): boolean => {
  let found = false
  Object.values(cast.scenes).forEach(scene => {
    if (scene.contents === undefined) return
    found = found || scene.contents[assetId] !== undefined
  })
  return found
}

const state = {
  inCast: false,
  actionPalleteTab: ActionPalleteTabs.Assets,
  simpleMode: false,
  currentCast: {} as Cast,
  currentPlaylist: {} as PlaylistStatus,
  eventMessages: [],
  onlineUsers: onlineUsers,
  events: [],
  allevents: [],
  completedEvents: [],
  eventsLoaded: false,
  completedEventsLoaded: false,
  assets: [] as Asset[],
  sceneInPreview: '',
  cefEvent: false,
  cefMode: '',
  cefMixer: '',
  openScene: '',
  sceneFilter: '',
  currentCasterStream: '',
  localCameraStream: null,
  sourceStreams: {},
  casting: false,
  castReady: false,
  outputStreams: {},
  audioMix: AudioMixType.Program,
  programVolumes: {},
  liveEditVolumes: {},
  monitorVolumes: {},
  monitorMutes: {},
  talkbackVolumes: {},
  talkbackMutes: {},
  talkbackMode: TalkbackMode.Global,
  talkbackGlobalMute: false,
  talkbackGlobalVolume: 70,
  talkBack: {},
  customStreams: {},
  streamDocs: {},
  castTeamDoc: {} as Team,
  castTeam: [],
  castingAs: '',
  castEnded: false,
  streamNames: {},
  streamImages: {},
  casterNames: {},
  chatOpen: false,
  chatLastSeen: 0,
  createCastLargeMachine: false,
  fullscreen: false,
  watermark: WATERMARK_DEFAULT,
  activeCasts: {},
  selfMute: false,
  audioInit: false,
  isCaster: null,
  iosFront: false,
  isPreviewingOutput: false,
} as EventsState

interface EventSubscriptions {
  castTeams: { [teamId: string]: ProfilesUnsubscribeFunction }
  outputStreams: { [streamId: string]: UnsubscribeFunction }
  updateSourceStreamDocs: { [streamId: string]: UnsubscribeFunction }
}

interface SceneContents {
  contents: { [sceneid: string]: SceneContent }
}

export type EventsUnsubscribeFunction = () => void

export function NullUnsubscribe () {}

export interface ProfilesUnsubscribeFunction {
  profiles: { [profileId: string]: UnsubscribeFunction }
  teams: UnsubscribeFunction
}

const subscriptions = {
  castTeams: {},
  outputStreams: {},
  updateSourceStreamDocs: {}
} as EventSubscriptions

function unsubscribeCastTeamId (teamId: string) {
  if (subscriptions.castTeams[teamId]) {
    const teamUnsubscribe = subscriptions.castTeams[teamId].teams
    teamUnsubscribe()
    Object.values(subscriptions.castTeams[teamId].profiles).forEach(profile => {
      profile()
    })
    delete subscriptions.castTeams[teamId]
  }
}

function fillMissingCssObjects (layout: Layout, castId: string, sceneId: string): void {
  //Populates missing CSS objects with default values to prevent errors at presentation time.
  //These should never be coming up empty, but it apparently happens (CC-3530).
  if (!layout) return //No scene_layout passed in!
  const updates = {} as Layout
  if (!layout.hasOwnProperty('tweetcss')) updates.tweetcss = new TweetCSS()
  if (!layout.hasOwnProperty('instagramcss')) updates.instagramcss = new InstagramCSS()
  if (!layout.hasOwnProperty('castercss')) updates.castercss = new CasterCSS()
  if (!layout.hasOwnProperty('chatcss')) updates.chatcss = new ChatCSS()
  if (!layout.hasOwnProperty('simplecgcss')) updates.simplecgcss = {} as KeyDict<SimplecgCSS>
  if (!layout.hasOwnProperty('livechatcss')) updates.livechatcss = new LiveChatCSS()

  if (!isEmpty(updates)) {
    //Must use JSON conversion to remove custom classes,
    //since Firestore only accepts plain Objects,
    //and we want to retain use of TS typings
    const updatesPayload = JSON.parse(JSON.stringify(updates))

    //Update actual layout in db if there were any missing
    if (layout.id) fb.db.collection('layouts').doc(layout.id).set(updatesPayload, { merge: true })

    //Backfill empty objects for loaded scene, as above doesn't trigger updates for loaded scene
    if (castId && sceneId) {
      fb.db.collection('casts').doc(castId).set(
        {
          scenes: {
            [sceneId]: {
              scene_layout: updatesPayload
            }
          }
        },
        { merge: true })
    }
  }
}

const getters = {
  hasEmptySlots (_state: EventsState): boolean {
    const numCasters = Object.values(store.getters.events.activeInvitedCasters).length
    return numCasters < store.getters.events.maxAllowedCasters
  },
  numTakenSlots (_state: EventsState): number {
    const numberOfGuests = store.getters.events.guestsInSlots.length
    const numberAllGuests = Object.keys(store.getters.events.activeInvitedCasters).length
    const freeAnonymousSlots = store.getters.events.maxAnonymousGuests - numberOfGuests
    const freeAllCastersSlots = store.getters.events.maxAllowedCasters - numberAllGuests
    const minOpenSlots = Math.min(freeAnonymousSlots, freeAllCastersSlots)
    return store.getters.events.maxAnonymousGuests - minOpenSlots
  },
  guestsInSlots (state: EventsState): InvitedCaster[] {
    const allowedTriagesInSlots = [CallTriage.Accepted, CallTriage.InModeration, CallTriage.Unverified]
    return Object.values(state.currentCast.invited_casters).filter((caster) => {
      return caster.anonymous && allowedTriagesInSlots.includes(caster.triage) && !caster.deleted
    })
  },
  maxAnonymousGuests (state: EventsState): number {
    return state.currentCast.anonymous_caster_rules?.max_anonymous_casters ?? 0
  },
  maxAllowedCasters (state: EventsState): number {
    return state.currentCast.anonymous_caster_rules?.max_total_casters ?? 0
  },
  activeInvitedCasters (state: EventsState): KeyDict<InvitedCaster> {
    if (state.currentCast?.invited_casters === undefined) return {}
    return pickBy(state.currentCast.invited_casters, (value) => {
      if (value.deleted === true) return false
      if (value.triage === CallTriage.Rejected) return false
      if (value.triage === CallTriage.Assigned) return false
      return true
    })
  },
  totalNewMessages (state: EventsState, _getters, rootState): number {
    const messages = state.eventMessages
    const lastSeen = state.chatLastSeen
    const currentUserId = rootState.user.id || ''
    if (messages.length === 0) {
      return 0
    }

    return reduce(messages, (total, m, _key) => {
      const isNew = m.timestamp && m.timestamp.seconds && m.timestamp.seconds * 1000 > lastSeen && m.user_id !== currentUserId
      return (isNew) ? total + 1 : total
    }, 0)
  },
  playListVideoIds (state: EventsState): string[] {
    const allVideoIDs : string[] = []
    if (!state.currentCast || !state.currentCast.scenes) return []
    Object.values(state.currentCast.scenes).forEach(scene => {
      if (!scene.contents) return
      Object.values(scene.contents).forEach(asset => {
        if (asset.type === AssetType.Video && !allVideoIDs.includes(asset.id)) {
          allVideoIDs.push(asset.id)
        }
      })
    })
    return allVideoIDs
  },
  streamDoc: (state: EventsState) => (rawStreamId: string) : StreamDoc | undefined => {
    const streamDocs = state.streamDocs
    const streamId = (rawStreamId || '').split('_')[0] // remove '_lowres' suffixes etc

    if (!streamDocs || Object.values(streamDocs).length === 0 || !streamDocs.hasOwnProperty(streamId)) {
      return undefined
    }
    return streamDocs[streamId]
  },
  currentUserIsACaster (_state: EventsState, getters, rootState, _rootGetters): boolean {
    const activeInvitedCasters = getters.activeInvitedCasters
    const currentUserId = rootState.user.id || ''
    if (currentUserId === '' || activeInvitedCasters[currentUserId] === undefined) return false
    return true
  },
  currentCastID: state => (state.currentCast.id),
  castAssetGroups: (state: EventsState): KeyDict<AssetGroup> => {
    if (!state.currentCast.id) {
      return {}
    }
    const castgroups = state.currentCast.asset_groups
    return pickBy(store.state.assets.folderList, folder => castgroups[folder.id] === true)
  },
  // used for debugging
  availableStreams: (state: EventsState): any[] => {
    const activeScene = getters.activeScene(state)
    return Object.values(activeScene!.contents || {}).filter((o: any) => o.type === AssetType.Broadcast)
  },
  numIngests (state: EventsState): KeyDict<KeyDict<number>> {
    if (hasFeature(Feature.SELF_SERVE)) {
      return {
        '1080': {
          '30': 2
        },
        '720': {
          '30': 2
        }
      }
    }
    const isLargeMachine = state.currentCast?.largemachine ?? state.createCastLargeMachine
    return {
      '1080': {
        '30': isLargeMachine ? 6 : 3,
        '60': isLargeMachine ? 4 : 1
      },
      '720': {
        '30': isLargeMachine ? 8 : 4,
        '60': isLargeMachine ? 6 : 3
      }
    }
  },
  availableWidgets: (state: EventsState): AssetType[] => {
    const widgetTypes: AssetType[] = []
    if (!!state.currentCast.widgets) {
      Object.values(state.currentCast.widgets).forEach(widget => {
        widgetTypes.push(widget.type)
      })
    }
    return widgetTypes
  },
  activeScene (state: EventsState): Scene | undefined {
    if (!state.currentCast.scenes) return undefined
    if (!state.currentCast.activeScene) return undefined
    if (!state.currentCast.scenes[state.currentCast.activeScene]) return undefined
    return state.currentCast.scenes[state.currentCast.activeScene]
  },
  openScene (state: EventsState): Scene | undefined {
    if (!state.openScene) return undefined
    if (!state.currentCast.scenes) return undefined
    if (!state.currentCast.scenes[state.openScene]) return undefined
    return state.currentCast.scenes[state.openScene]
  },
  castInputs (state: EventsState) {
    let inputs: CastInputStream[] = []
    if (state.inCast) {
      for (const key in state.sourceStreams) {
        if (state.sourceStreams[key].type === 'broadcast') {
          if (state.streamNames.hasOwnProperty(key)) {
            const input: CastInputStream = {
              id: key,
              connected: false,
              name: state.streamNames[key]
            }
            input.name = state.streamNames[key]
            if (state.sourceStreams[key].ngcvp_status) {
              input.connected = state.sourceStreams[key].ngcvp_status === 'green'
            } else {
              input.connected = false
            }
            inputs.push(input)
          }
        }
      }
      inputs = sortBy(inputs, [
        o => {
          return o.name.toLowerCase()
        }
      ])
    }
    return inputs
  },
  currentMuteLevels (state: EventsState, getters): KeyDict<boolean> {
    const muteStatus = {}
    if (getters.activeScene === null || getters.activeScene === undefined) {
      return muteStatus
    }
    const activeScene = getters.activeScene
    for (const key in state.programVolumes) {
      const tmpkey = key.split('_')
      if (tmpkey.length === 1 && activeScene.contents[key] !== undefined) {
        muteStatus[key] = activeScene.contents[key].muted === undefined ? false : activeScene.contents[key].muted
      } else if (activeScene.contents[tmpkey[0]] !== undefined) {
        if (tmpkey[1] === 'left') {
          muteStatus[key] =
            activeScene.contents[tmpkey[0]].leftMuted === undefined ? true : activeScene.contents[tmpkey[0]].leftMuted
        } else {
          muteStatus[key] =
             activeScene.contents[tmpkey[0]].rightMuted === undefined ? false : activeScene.contents[tmpkey[0]].rightMuted
        }
      }
    }
    return muteStatus
  },
  selectedAudioLevels (state: EventsState, getters): KeyDict<number> {
    let volumes = JSON.parse(JSON.stringify(state.programVolumes))
    if (state.audioMix === AudioMixType.Monitor) {
      volumes = state.monitorVolumes
    } else if (state.audioMix === AudioMixType.LiveEdit) {
      for (const key in volumes) {
        if (state.liveEditVolumes[key] !== undefined) {
          volumes[key] = state.liveEditVolumes[key]
        } else {
          // REFACTOR >>> Fairly similar to AudioControls::isInActiveScene()
          let isActiveInScene = false
          const keyBase = key.split('_')[0]
          const activeScene = getters.activeScene
          if (activeScene !== undefined && activeScene.contents !== undefined && activeScene.contents[keyBase] !== undefined) {
            if (activeScene.contents[keyBase].active && activeScene.scene_layout.boxes[activeScene.contents[keyBase].box] !== undefined) {
              isActiveInScene = true
            }
          }
          if (key === 'playlist') {
            isActiveInScene = true
          }
          // REFACTOR <<<
          if (getters.currentMuteLevels[key] || !isActiveInScene) {
            volumes[key] = 0
          }
        }
      }
    } else {
      for (const key in volumes) {
        if (getters.currentMuteLevels[key]) {
          volumes[key] = 0
        }
      }
    }
    return volumes
  },
  invitedCasters (state: EventsState): KeyDict<InvitedCaster> {
    const casters = state.currentCast.hasOwnProperty('invited_casters') ? state.currentCast.invited_casters : {}

    for (const [key, value] of Object.entries(casters)) {
      if (typeof value === 'boolean') {
        casters[key] = new InvitedCaster({ userId: key })
      } else {
        casters[key] = new InvitedCaster({ ...casters[key], userId: key })
      }
    }

    return casters
  },

}

interface RequestCustomAudioMix {
  castId: string
  casterId: string
  volumes: KeyDict<number>
  audioMixType: AudioMixType
}

interface RequestSwitchToAudioMix {
  castId: string
  casterId: string
  audioMixType: AudioMixType
}

interface SetCasterStreamInfo {
  streamId: string
  type: StreamType
  sessionId: string
  userId: string
}

const actions = {
  async subscribeCurrentPlaylist (context: EventsContext, castId: string) {
    const { commit } = eventsModuleActionContext(context)
    const snapper: FirestoreDocSnapper = {
      doc: fb.db.collection('playliststatus').doc(castId),
      mutation: commit.updateCurrentPlaylist,
      type: FirestoreSnapperType.Document
    }
    storehelper.subscribe('currentplaylist', snapper)
  },
  async unsubscribeCurrentPlaylist (_context: EventsContext) {
    storehelper.unsubscribe('currentplaylist')
  },
  async subscribeCurrentCast (
    context: EventsContext,
    castId: string) {
    const { commit, dispatch } = eventsModuleActionContext(context)
    // FIXME -> this needs to go, components needs to subscribe themselfe
    dispatch.subscribeCurrentPlaylist(castId)
    // -------------------------
    const mutation = (doc) => {
      commit.updateCurrentCast(doc)
    }
    const snapper: FirestoreDocSnapper = {
      doc: fb.db.collection('casts').doc(castId),
      mutation: mutation,
      type: FirestoreSnapperType.Document
    }
    const waitForIt = true
    await storehelper.subscribe('currentcast', snapper, undefined, waitForIt)
  },
  async unsubscribeCurrentCast (context: EventsContext) {
    const { dispatch } = eventsModuleActionContext(context)
    storehelper.unsubscribe('currentcast')
    await dispatch.unsubscribeCurrentPlaylist()
  },
  async subscribeCurrentCastCef (context: EventsContext, castId: string) {
    const { commit, dispatch } = eventsModuleActionContext(context)
    // FIXME -> this needs to go, components needs to subscribe themselfe
    dispatch.subscribeCurrentPlaylist(castId)
    // -------------------------
    const snapper: FirestoreDocSnapper = {
      doc: fb.db.collection('casts').doc(castId),
      mutation: commit.updateCurrentCast,
      type: FirestoreSnapperType.Document
    }
    storehelper.subscribe('currentcastCef', snapper)
  },
  async unsubscribeCurrentCastCef (_context: EventsContext) {
    storehelper.unsubscribe('currentcastCef')
  },
  async subscribeEvents (context: EventsContext, teamId: string) {
    const { commit } = eventsModuleActionContext(context)
    const now = new Date()
    const snapper: FirestoreQuerySnapper = {
      query: fb.db
               .collection('events')
               .where('team_id', '==', teamId)
               .where('end_date', '>', now)
               .where('deleted', '==', false),
      mutation: commit.updateEvents,
      type: FirestoreSnapperType.Query
    }
    const waitForIt = true
    storehelper.subscribe('currentevents', snapper, undefined, waitForIt)
  },
  async unsubscribeEvents (_context: EventsContext) {
    storehelper.unsubscribe('currentevents')
  },
  async updateSubscriptionCompletedEvent (_context: EventsContext, teamId: string) {
    const query = await createCompletedEventsQuery(teamId)
    storehelper.updateQuery('completedevents', query)
  },
  async subscribeCompletedEvent (context: EventsContext, teamId: string) {
    const { commit } = eventsModuleActionContext(context)
    const query = await createCompletedEventsQuery(teamId)
    const snapper: FirestoreQuerySnapper = {
      query,
      mutation: commit.updateCompletedEvents,
      type: FirestoreSnapperType.Query
    }
    storehelper.subscribe('completedevents', snapper)
  },
  async unsubscribeCompletedEvent (_context: EventsContext) {
    storehelper.unsubscribe('completedevents')
  },
  async subscribeDumpStateRequests (context: EventsContext, castId: string) {
    const { commit } = eventsModuleActionContext(context)
    const snapper : FirestoreDocSnapper = {
      doc: fb.db.collection('cast_dumps').doc(castId),
      mutation: commit.updateDumpStateRequests,
      type: FirestoreSnapperType.Document
    }
    const key = `cast_dumps/${castId}`
    storehelper.subscribe(key, snapper)
  },
  /*async */ unsubscribeDumpRequests (_context: EventsContext, castId: string){
    const key = `dumpRequests:${castId}`
    storehelper.unsubscribe(key)
  },
  async requestDumpState (_context: EventsContext, params: { castId:string, teamId:string } ) : Promise<void> {
    console.log('requestDumpState params=', params)
    const now = '' + (new Date().getTime())
    const update = {
      [now] : {},
      team_id: params.teamId
    }
    try{
      const docRef = fb.db.collection('cast_dumps').doc(params.castId)
      await docRef.set(update, { merge : true })
    } catch(error){
      console.log('requestDumpState', error)
    }
  },
  async subscribeActiveCasts (context: EventsContext, teamId: string) {
    const { commit } = eventsModuleActionContext(context)
    const now = new Date()
    const snapper: FirestoreQuerySnapper = {
      query: fb.db
               .collection('casts')
               .where('team_id', '==', teamId)
               .where('end_date', '>', now)
               .where('deleted', '==', false),
      mutation: commit.updateActiveCasts,
      type: FirestoreSnapperType.Query
    }
    storehelper.subscribe(`currentcasts`, snapper)
  },
  async unsubscribeActiveCasts (_context: EventsContext) {
    storehelper.unsubscribe(`currentcasts`)
  },
  async subscribeMessages (context: EventsContext, chatId: string) {
    const { commit } = eventsModuleActionContext(context)
    const snapper: FirestoreQuerySnapper = {
      query: fb.db
               .collection('chats')
               .doc(chatId)
               .collection('messages'),
      mutation: commit.updateMessages,
      type: FirestoreSnapperType.Query
    }
    storehelper.subscribe('chats', snapper)
  },
  async unsubscribeMessages (_context: EventsContext) {
    storehelper.unsubscribe('chats')
  },
  async clearCurrentCast (context: EventsContext) {
    // FIXME needs to go, each thing should unsubscribe itself
    const { dispatch } = eventsModuleActionContext(context)
    dispatch.unsubscribeCurrentCast()
    dispatch.unsubscribeCurrentPlaylist()
    dispatch.unsubscribeMessages()
    dispatch.unsubscribeEvents()
  },
  //
  setInCast (context: EventsContext, flag: boolean) {
    const { commit } = eventsModuleActionContext(context)
    commit.setInCast(flag)
  },
  async cefHeartbeat (_state: EventsContext, instanceData: any) {
    if (!instanceData || !instanceData.castId || !instanceData.cefmode) {
      return
    }

    const documentId = instanceData.castId + '_' + instanceData.cefmode.toLowerCase()
    const documentData = {
      castId: instanceData.castId,
      cefmode: instanceData.cefmode,
      instances: {
        [instanceData.key]: instanceData
      }
    }
    try {
      await fb.db
        .collection('cefstats')
        .doc(documentId)
        .set(documentData, { merge: true })
    } catch (error) {
      console.error('cef heartbeat error ', error)
    }
  },
  setEditCastLargeMachine (context: EventsContext, largemachine: boolean) {
    const { commit } = eventsModuleActionContext(context)
    commit.setEditCastLargeMachine(largemachine)
  },
  updateCurrentCastFromLocalStorage (context: EventsContext, payload: string | null) {
    if (payload) {
      const { commit } = eventsModuleActionContext(context)
      commit.updateCurrentCastFromDoc(JSON.parse(payload))
    }
  },
  async createEvent (_context: EventsContext, event: CloudCastEvent) {
    try {
      const now = (new Date()).getTime()
      const db = fb.db
      const teamRef = db.collection('teams').doc(event.team_id)
      const eventRef = db.collection('events').doc() // generated client side...

      const eventBatch = db.batch()
      eventBatch.set(eventRef, event)
      eventBatch.set(teamRef, { lastEventTimestamp: now}, {merge : true})
      await eventBatch.commit()
    } catch (error) {
      console.error('Error adding event: ', error)
    }
  },
  makeClip (_context: EventsContext, event) {
    return new Promise<void>(resolve => {
      const data = {}
      const id = 'clip' + new Date().getTime()
      data[event.cast] = {}
      data[event.cast][id] = {
        k360event: event.k360event,
        mode: 'clip',
        start: event.start,
        end: event.end,
        name: event.name
      }
      fb.db
        .collection('admin')
        .doc('mp4create')
        .set(data, { merge: true })
        .then(() => {
          resolve()
        })
        .catch(error => {
          console.error('Make clip error ', error)
        })
      const data2 = { clips: {} }
      data2.clips[id] = {
        start: event.start,
        end: event.end,
        name: event.name,
        url: 'running'
      }
      fb.db
        .collection('casts')
        .doc(event.cast)
        .set(data2, { merge: true })
        .then(() => {
          resolve()
        })
        .catch(error => {
          console.error('Make Clip error ', error)
        })
    })
  },
  deleteClip (_context, event) {
    return new Promise(() => {
      const data = { clips: {} }
      data.clips[event.clipid] = { deleted: true }
      fb.db
        .collection('casts')
        .doc(event.cast)
        .set(data, { merge: true })
    })
  },
  async updateEvent (context: EventsContext, event: CloudCastEvent) {
    const { dispatch } = eventsModuleActionContext(context)
    try {
      // TODO: Get rid of EventDate(/firestore.Timestamp) and just always use Date.
      const endDate = event.end_date.seconds !== undefined ? new Date(event.end_date.seconds * 1000) : event.end_date as unknown as Date
      // @ts-ignore
      event.end_date = helpers.getAtLeastStartDate(event, endDate)
      await fb.db.collection(CollectionNames.EVENTS).doc(event.id).set(event, { merge: true })
      dispatch.updateCastStartAndEnds(event)
    } catch (error) {
      console.error('Error updating event: ', error)
    }
  },
  async updateCastStartAndEnds (_context: EventsContext, event: CloudCastEvent) {
    try {
      const results = await fb.db
        .collection('casts')
        .where('team_id', '==', event.team_id)
        .where('event_id', '==', event.id)
        .where('deleted', '==', false)
        .get()
      const nowSeconds = Date.now() / 1000
      for (let i = 0; i < results.docs.length; i++) {
        const cast = results.docs[i].data() as Cast
        if (cast.end_date.seconds > nowSeconds) {
          const startDateEvent = !!event.start_date ? moment(event.start_date?.seconds ? event.start_date.seconds * 1000 : event.start_date) : null
          const startDateCast = cast.start_date.seconds
          const needsApproval = !!startDateEvent?.isBefore(moment().add(2, 'days'))
          //Set up payload with only properties that should be changed via spread operator
          const updates: Partial<Cast> = {}
          //Include new 'end_date' if not missing from event
          if (event.end_date) {
            updates.end_date = event.end_date
          }
          //Include new 'start_date' unless cast already started
          if (startDateCast > nowSeconds && startDateEvent !== null) {
            updates.start_date = moment.max(moment(nowSeconds * 1000), startDateEvent).toDate() // future event start, or now
          }
          //Include new 'pendingPlatinumRequest' only if relevant to the cast
          if (!cast.platinumApproved && cast.supportLevel === SupportLevel.Platinum) {
            updates.pendingPlatinumRequest = needsApproval
          }
          if (!isEmpty(updates)) {
            await results.docs[i].ref.set(updates, { merge: true }).catch(error => {
              console.error('Update Cast Start & End and Platinum Pending', error)
            })
          }
        }
      }
    } catch(error) {
      console.error('Update Cast Start & Ends and Platinum Pending', error)
    }
  },
  async createCast (_context: EventsState, castParams: Partial<Cast>) : Promise<string> {
    let savedCast
    try {
      if (castParams.team_id === undefined) throw new Error('New cast must have a team id.')
      const teamId = castParams.team_id

      // Note: A Transaction doesn't have `add()`, so we need to get a ref first and then use `set()`
      // Note: A Transaction requires all reads to happen before the writes.
      const releaseDocRef = fb.db.collection(CollectionNames.ADMIN).doc(AdminDocNames.RELEASE)
      const chatDocRef = fb.db.collection(CollectionNames.CHATS).doc()
      const castDocRef = fb.db.collection(CollectionNames.CASTS).doc()
      const videoDocRef = fb.db.collection(CollectionNames.VIDEO_PLAYER).doc(castDocRef.id)
      const widgetDocRef = fb.db.collection(CollectionNames.WIDGETS).doc()
      const eventDocRef = fb.db.collection(CollectionNames.EVENTS).doc()
      const teamDocRef = fb.db.collection(CollectionNames.TEAMS).doc(teamId)

      return await fb.db.runTransaction(async (transaction: firestore.Transaction) => {
        let defaultVersion = '1.0.0'
        //let defaultVersion = store.state.admin.release?.default ?? '1.0.0' // Do we need to `subscribeRelease()`?
        // FIXME: Because of merging issues I now collect this from the db directly. A better approuch is to get it
        // from somewhere else in the store, so this can be rewritten as soon as those changes are on dev or stage
        const releases = await transaction.get(releaseDocRef)
        if (releases.exists) {
          const data = releases.data()
          if (data?.default) defaultVersion = data.default
        }
        const version = store.state.team.team.version ?? defaultVersion

        const eventWithId: CloudCastEvent = {
          id: '',
          team_id: teamId,
          name: castParams.name,
          asset_groups: {},
          deleted: false,
          start_date: castParams.start_date,
          end_date: castParams.end_date
        }
        const event: Partial<CloudCastEvent> = eventWithId
        delete event.id

        transaction.set(eventDocRef, event)
        transaction.set(teamDocRef, { lastEventTimestamp: Date.now() }, { merge: true })

        transaction.set(chatDocRef, { team_id: castParams.team_id, messages: [] })
        let baseScenes: Partial<Scene> = {}
        const sourceStreams = {
          playlist: {
            active: true,
            type: 'playlist',
            volume: 0
          }
        }
        let activeScene: string|null = null
        if (castParams.copying || (castParams.scenes !== undefined && Object.keys(castParams.scenes).length > 0)) {
          if (castParams.scenes) {
            baseScenes = castParams.scenes
          }
          activeScene = castParams.activeScene ?? Object.keys(baseScenes)[0]
        } else {
          activeScene = helpers.makeId()

          const teamLayouts = store.state.team.teamLayouts
          const defaultTeamLayoutId = store.state.team.defaultTeamLayout
          const defaultLayout = teamLayouts.find(layout => {
            return layout && layout.id === defaultTeamLayoutId
          })
          if (!defaultLayout) {
            console.error('Team of cast has no default layout!', castParams)
          }

          // ADD LAYOUT CONTENT
          let defaultContents: KeyDict<SceneContent> = {}
          if (defaultLayout?.contents !== undefined) {
            defaultContents = {
              ...defaultLayout.contents
            }
          }

          baseScenes[activeScene] = {
            active: true,
            contents: defaultContents,
            name: 'Scene 1',
            id: activeScene,
            status: 'active',
            layout_id: defaultLayout?.name ?? 'unknown',
            scene_layout: defaultLayout ?? {}
          }
        }

        const baseStream = {
          active: true,
          type: 'broadcast',
          volume: 0,
          volumeLeft: 0,
          volumeRight: 0,
          leftMuted: true,
          rightMuted: false
        }

        // loop through and add the streams we defined
        if (castParams.input_streams !== undefined) {
          Object.keys(castParams.input_streams).forEach(key => {
            sourceStreams[key] = baseStream
          })
        }

        // cleanup all the scenes broadcast streams to make sure they match what has been chosen
        if (castParams.scenes !== undefined) {
          for (const scene of Object.values(castParams.scenes)) {
            if (scene.contents) {
              for (const key in scene.contents) {
                const tmpContent = scene.contents[key]
                if (tmpContent.type === AssetType.Broadcast && castParams.input_streams !== undefined && castParams.input_streams[tmpContent.id] === undefined) {
                  delete scene.contents[key]
                }
              }
            }
          }
        }

        if (castParams.copying) {
          if (castParams.id !== undefined) delete castParams.id
          if (castParams.k360_event_id !== undefined) delete castParams.k360_event_id
          if (castParams.status !== undefined) delete castParams.status
          if (castParams.ngcvp_status !== undefined) delete castParams.ngcvp_status
          if (castParams.m3u8 !== undefined) delete castParams.m3u8
          if (castParams.video_start_time !== undefined) delete castParams.video_start_time
          if (castParams.moderators !== undefined && Object.keys(castParams.moderators).length > 0) castParams.moderators = {}
        }

        castParams.anonymous_caster_rules = {
          max_anonymous_casters: 5,
          max_total_casters: 10
        }
        if (hasFeature(Feature.SELF_SERVE)) {
          castParams.anonymous_caster_rules = {
            max_anonymous_casters: 4,
            max_total_casters: 4
          }
        }

        const token = helpers.generateRandomString(24)

        savedCast = {
          ...castParams,
          invited_casters: Convertable.toObject(castParams.invited_casters),
          chat_id: chatDocRef.id,
          online_sessions: {},
          activeScene,
          scenes: baseScenes,
          widgets: {},
          source_streams: sourceStreams,
          version,
          creator_id: store.getters.user.currentUserId,
          token,
          event_id: eventDocRef.id
        }

        transaction.set(castDocRef, savedCast)
        savedCast.id = castDocRef.id

        const widgets = {}
        if (!castParams.copying && castParams.widgets && Object.values(castParams.widgets).length) {
          for (const widget of Object.values(castParams.widgets as KeyDict<Widget>)) {
            const isNewWidget = widget.id === widget.type && widget.type === 'chat'
            if (isNewWidget && castParams.team_id) {
              widget.id = await store.dispatch.chatwidget.createWithTransaction({
                transaction,
                docRef: widgetDocRef,
                data: {
                  teamid: castParams.team_id,
                  castid: savedCast.id
                }
              })
            }
            widgets[widget.id] = JSON.parse(JSON.stringify(widget))
          }
        }

        const vp: VideoPlayer = new VideoPlayer('player1')
        // TODO: Do we really want to save the id property to the DB?
        transaction.set(castDocRef, { id: savedCast.id, widgets }, { merge: true })
        transaction.set(videoDocRef, { 'player1': vp.toJSON(), team_id: castParams.team_id }, { merge: true })
        return castDocRef.id
      })
    } catch (error) {
      console.error(`Error creating cast ${error}`, savedCast)
    }

    return Promise.reject()
  },
  async copyCast (_context, castCopy) {
    try {
      // * CREATE *
      // create a minimal new Cast
      castCopy.copying = true
      if (castCopy.start_date < new Date()) {
        castCopy.start_date = new Date()
      }
      const castId = await store.dispatch.events.createCast(castCopy)
      const createdCastDoc = await fb.db
        .collection('casts')
        .doc(castId)
        .get()
      const createdCast = createdCastDoc.data()
      if (!createdCast || !castId) {
        console.error('Error while copying - Failed to create event')
        return
      }

      // * UPDATE *
      // Add properties as needed
      createdCast.id = castId

      // update with original cast info:
      const originalCast = JSON.parse(castCopy.originalCast)
      const widgets = JSON.parse(JSON.stringify(originalCast.widgets || {}))
      createdCast.widgets = widgets

      // sourcestreams
      const all_sourceStreams = JSON.parse(JSON.stringify(originalCast.source_streams))
      const sourceStreams = reduce(
        all_sourceStreams,
        function (result, value, key) {
          if (key === 'playlist' || value.type === AssetType.Broadcast) {
            // copy specific properties only
            result[key] = {
              active: !!value.active,
              type: value.type,
              volume: value.volume || 0,
              volumeLeft: value.volumeLeft || 0,
              volumeRight: value.volumeRight || 0,
              leftMuted: !!value.leftMuted,
              rightMuted: !!value.rightMuted
            }
          }
          return result
        },
        {}
      )
      createdCast.source_streams = sourceStreams
      createdCast.copying = false
      await store.dispatch.events.updateCast(createdCast)
    } catch (error) {
      console.error(`Error copying event ${error}`)
    }
  },
  async copyCastOutputs (_context, castCopy) {
    try {
      const copiedCastDoc = await fb.db
        .collection('casts')
        .doc(castCopy.id)
        .get()
      const copiedCast = copiedCastDoc.data()
      if (!copiedCast) {
        return
      }

      // get latest original cast (internal copy might be behind, eg deleted etc)
      const originalCast = JSON.parse(copiedCast.originalCast)
      const originalCastDoc = await fb.db
        .collection('casts')
        .doc(originalCast.id)
        .get()
      let originalCastData = originalCastDoc.data()
      if (originalCastData) {
        copiedCast.custom_rtmp_destinations = cloneDeep(originalCastData.custom_rtmp_destinations || {})
        await store.dispatch.events.updateCast(copiedCast)

        originalCastData = {
          ...originalCastData,
          custom_rtmp_destinations: {}
        }
        await store.dispatch.events.updateCast(originalCastData)
      }

    } catch (error) {
      console.error(`Error copy event ${error}`)
    }
  },
  async requestCasterRepublish (_context: EventsContext, cast: {id: string, republishRequested: KeyDict<boolean>}) {
    try {
      await fb.db.collection('casts').doc(cast.id).set(cast, { merge: true })
    } catch (error) {
      console.error('requestCasterRepublish error', error)
    }
  },
  // TODO: try-catches
  async clearRequestedCustomAudioMix (_context: EventsContext, params: { castId: string, casterId: string }) {
    try {
      await fb.db.collection('casts').doc(params.castId).update({
        [`requestCustomAudioMix.${params.casterId}`]: fb.firebase.firestore.FieldValue.delete()
      })
    } catch (error) {
      console.error('clearRequestedCustomAudioMix error', error)
    }
  },
  async requestCasterCustomAudioMix (_context: EventsContext, params: RequestCustomAudioMix) {
    try {
      await fb.db.collection('casts').doc(params.castId).set({
        requestCustomAudioMix: {
          [params.casterId]: {
            volumes: params.volumes,
            audioMixType: params.audioMixType
          }
        }
      }, { merge: true })
    } catch (error) {
      console.error('requestCasterCustomAudioMix error', error)
    }
  },
  async clearRequestedSwitchToAudioMix (_context: EventsContext, params: { castId: string, casterId: string }) {
    try {
      await fb.db.collection('casts').doc(params.castId).update({
        [`requestSwitchToAudioMix.${params.casterId}`]: fb.firebase.firestore.FieldValue.delete()
      })
    } catch (error) {
      console.error('clearRequestedSwitchToAudioMix error', error)
    }
  },
  async requestCasterSwitchToAudioMix (_context: EventsContext, params: RequestSwitchToAudioMix) {
    try {
      await fb.db.collection('casts').doc(params.castId).set({
        requestSwitchToAudioMix: {
          [params.casterId]: params.audioMixType
        }
      }, { merge: true })
    } catch (error) {
      console.error('requestCasterSwitchToAudioMix error', error)
    }
  },
  restartClipsplayback (_context, cast) {
    return new Promise<void>(resolve => {
      fb.db.collection('casts').doc(cast.id).set(
        {
          source_streams: {
            playlist: {
              delete: true
            }
          }
        },
        { merge: true }
      ).then(() => {
        resolve()
      }).catch(error => {
        console.error('Could not restart Clips playback', error)
      })
    })
  },
  async restartCef (_context: EventsContext, castid: string) {
    try {
      await fb.db.collection('casts').doc(castid).set(
        { restart_cef: true},
        { merge: true })
    } catch (error) {
      console.error('Could not restart Cef', error)
    }
  },
  setStreamNames (context: EventsContext) {
    const { commit } = eventsModuleActionContext(context)
    commit.setStreamNames({ teamStreams: store.state.team.streams })
  },
  async activateWidget (_context, { widget, active, castid }) {
    await fb.db
      .collection('casts')
      .doc(castid)
      .set(
        {
          widgets: {
            [widget]: { active }
          }
        },
        { merge: true }
      )
  },
  async addClipToScenes (context: EventsContext, payload: { assetId: string, name: string }): Promise<boolean> {
    const { state } = eventsModuleActionContext(context)
    if (!state.currentCast) return false
    const updates = {scenes: {}}
    let maxOrder = 0
    Object.values(state.currentCast.scenes).forEach(scene => {
      if (scene.contents === undefined) return
      Object.values(scene.contents).filter(content => content.type === AssetType.Video).forEach(content => {
        maxOrder = Math.max(content.order, maxOrder)
      })
    })
    for (const [sceneid, scene] of Object.entries(state.currentCast.scenes)) {
      Object.keys(scene.scene_layout.boxes).forEach(boxid => {
        const types = scene.scene_layout.boxes[boxid].types
        if (types && types.includes(AssetType.Video)) {
          if (updates.scenes[sceneid] === undefined) {
            updates.scenes[sceneid] = {contents: {}}
          }
          updates.scenes[sceneid].contents[payload.assetId] = {
            active: false,
            box: boxid,
            id: payload.assetId,
            name: payload.name,
            type: AssetType.Video,
            order: maxOrder + 1
          }
        }
      })
    }
    try {
      if (Object.keys(updates.scenes).length > 0) {
        await fb.db.collection('casts').doc(state.currentCast.id).set(updates, {merge: true})
      }
      // else no scenes to be updated -> if we pass empty dict, no
    } catch (error) {
      console.error('Error add ClipToScenes', error)
      return false
    }
    return true
  },
  async inviteCasterToOtherCast (_context: EventsContext, params: { castId: string, casterId: string }) {
    const invitedCaster = new InvitedCaster({ userId: params.casterId })
    await fb.db.collection('casts').doc(params.castId).set({
      invited_casters: {
        [params.casterId]: invitedCaster.toObject()
      }
    }, {merge: true})
  },
  async updateCastAndBillingCast (_context: EventsContext,
    params: { castId: string, billingIgnore: boolean, teamId: string, monthKey: string }) {

    try{
      const detailsUpdate = {
        billing_ignore: params.billingIgnore,
        last_update: firestore.FieldValue.serverTimestamp ()
      }
      await fb.db.collection('billing_details')
                 .doc(params.teamId)
                 .collection(params.monthKey)
                 .doc(params.castId)
                 .set(detailsUpdate, { merge: true })
      await fb.db.collection('casts')
                .doc(params.castId)
                .set({billing_ignore : params.billingIgnore},
                     { merge : true })
    } catch (error){
      console.log(error)
    }
  },
  async updateCast (_context: EventsContext, cast: Partial<Cast>): Promise<void> {
    if (!cast.id) return Promise.reject()
    // remember cast param can be a partial cast
    const origCastDoc = await fb.db
      .collection(CollectionNames.CASTS)
      .doc(cast.id)
      .get()
    // TODO: Properly convert firebase data to Cast object.
    const origCast = origCastDoc.data() as Cast|undefined
    if (!origCast) {
      return Promise.reject()
    }

    // Handle deleted items
    // need to clear this field because its a merged updated - this is ugly, we should fix it somewhere else
    const deleteupdate = {}
    for (const caster in origCast.invited_casters) {
      if (cast.invited_casters !== undefined && !(caster in cast.invited_casters)) {
        deleteupdate['invited_casters.' + caster] = fb.firebase.firestore.FieldValue.delete()
      }
    }
    if (cast.input_streams !== undefined) {
      for (const input in origCast.input_streams) {
        if (!(input in cast.input_streams)) {
          deleteupdate['input_streams.' + input] = fb.firebase.firestore.FieldValue.delete()
        }
      }
    }
    if (cast.source_streams !== undefined && cast.input_streams !== undefined) {
      for (const key in cast.source_streams) {
        const input = cast.source_streams[key]
        if (input.type === StreamType.Broadcast && !(key in cast.input_streams)) {
          deleteupdate['source_streams.' + key] = fb.firebase.firestore.FieldValue.delete()
          delete cast.source_streams[key]
        }
      }
    }
    if (origCast.asset_groups !== undefined && cast.asset_groups !== undefined)
    for (const assetgroup in origCast.asset_groups) {
      if (!(assetgroup in cast.asset_groups)) {
        deleteupdate['asset_groups.' + assetgroup] = fb.firebase.firestore.FieldValue.delete()
      }
    }
    Object.keys(origCast.widgets ?? {}).forEach(key => {
      if (cast.widgets !== undefined && !(key in cast.widgets)) {
        deleteupdate['widgets.' + key] = fb.firebase.firestore.FieldValue.delete()
      }
    })

    const updateWidgets = JSON.parse(JSON.stringify(cast.widgets || {}))
    cast.widgets = {}
    if (updateWidgets && Object.values(updateWidgets).length) {
      for (const widget of Object.values(updateWidgets as KeyDict<Widget>) as Widget[]) {
        const missingId = !widget.id || widget.id === widget.type
        if (missingId) {
          widget.id = await store.dispatch.chatwidget.create({
            teamid: cast.team_id ?? origCast.team_id,
            castid: cast.id
          })
        }
        cast.widgets[widget.id] = JSON.parse(JSON.stringify(widget))
        console.log('widget.id update', widget.id, widget)
      }
    }
    //
    // if we don't have the source_stream made from the input_streams add them
    //
    if (cast.input_streams !== undefined) {
      cast.source_streams = cast.source_streams ?? {}
      Object.keys(cast.input_streams).forEach(key => {
        if (cast.source_streams![key] === undefined) {
          const baseStream = {
            active: true,
            type: 'broadcast',
            start_time: new Date().getTime() / 1000,
            volume: 0,
            volumeLeft: 0,
            volumeRight: 0
          } as SourceStream

          cast.source_streams![key] = baseStream
        }
      })
    }
    //

    if (cast.start_date?.seconds !== undefined) {
      cast.start_date = new Date(cast.start_date.seconds * 1000)
    }
    if (cast.end_date?.seconds !== undefined) {
      cast.end_date = new Date(cast.end_date.seconds * 1000)
    }

    if (cast.start_date !== undefined && cast.end_date !== undefined) {
      if (cast.end_date < cast.start_date) {
        console.warn('Cast has end_date that is before start_date', cast)
        cast.end_date = cast.start_date
      }
    }

    if (cast.invited_casters !== undefined) cast.invited_casters = Convertable.toObject(cast.invited_casters)

    await fb.db.collection(CollectionNames.CASTS).doc(cast.id).update(deleteupdate)
    await fb.db.collection(CollectionNames.CASTS).doc(cast.id).set(cast, { merge: true })

    // Update event if needed.
    const eventId = cast.event_id // We also consider empty string as invalid here.
    if (!eventId) {
      console.error('Cast does not have a valid event_id', cast)
    } else {
      const eventUpdates: Partial<CloudCastEvent> = {}
      if (cast.start_date !== undefined && origCast.start_date !== undefined) {
        if (origCast.start_date.seconds !== undefined) origCast.start_date = new Date(origCast.start_date * 1000)
        if (cast.start_date.getTime() != origCast.start_date.getTime()) {
          eventUpdates.start_date = cast.start_date
        }
      }
      if (cast.end_date !== undefined && origCast.end_date !== undefined) {
        if (origCast.end_date.seconds !== undefined) origCast.end_date = new Date(origCast.end_date * 1000)
        if (cast.end_date.getTime() != origCast.end_date.getTime()) {
          eventUpdates.end_date = cast.end_date
        }
      }
      if (cast.name && cast.name !== origCast.name) {
        eventUpdates.name = cast.name
      }
      if (Object.keys(eventUpdates).length > 0) {
        await fb.db.collection(CollectionNames.EVENTS).doc(eventId).set(eventUpdates, { merge: true })
      }
    }
  },
  updateCastPlaylistStatus (_context, cast) {
    return new Promise<void>(resolve => {
      if (cast === undefined || cast.id === undefined) {
        resolve()
      } else {
        fb.db
          .collection('playlistconnections')
          .doc(cast.id)
          .set(cast, { merge: true })
          .then(() => {
            resolve()
          })
          .catch(error => {
            console.error('Update Cast Playlist Status error ', error)
          })
      }
    })
  },
  unsubscribeUpdateSourceStreamDocs (_context) {
    Object.values(subscriptions.updateSourceStreamDocs).forEach(unsubscriptions => {
      unsubscriptions()
    })
    subscriptions.updateSourceStreamDocs = {}
  },
  updateSourceStreamDocs (context: EventsContext) {
    const { commit, state, dispatch } = eventsModuleActionContext(context)
    dispatch.unsubscribeUpdateSourceStreamDocs()
    for (const key in state.sourceStreams) {
      if (key !== 'playlist') {
        if (key in subscriptions.updateSourceStreamDocs) {
          console.error('warning updateSourceStreamDocs ' + key + ' : duplicate key')
        }
        subscriptions.updateSourceStreamDocs[key] = fb.db
          .collection('streams')
          .doc(key)
          .onSnapshot(
            snap => {
              commit.streamDocLoaded({ data: snap.data() as Stream, id: key })
            },
            error => {
              console.error('error updateSourceStreamDocs ' + key + ' ' + error)
            }
          )
      }
    }
  },
  async removeAssetGroupFromCast ({ state }, assetGroupId: string) {
    const update = { asset_groups: { [assetGroupId]: false } }
    await fb.db
      .collection('casts')
      .doc(state.currentCast.id)
      .set(update, { merge: true })
    await store.dispatch.assets.selectFolder('')
  },
  async updateCastAssetGroups ({ state }, assetGroups: KeyDict<boolean>) {
    await fb.db
      .collection('casts')
      .doc(state.currentCast.id)
      .update({ asset_groups: assetGroups })
  },
  unsubscribeUpdateOutputStreams (_context) {
    Object.values(subscriptions.outputStreams).forEach(streamFunc => {
      streamFunc()
    })
    subscriptions.outputStreams = {}
  },
  updateOutputStreams (context: EventsContext) {
    const { commit, state, dispatch } = eventsModuleActionContext(context)
    if (state.outputStreams) {
      dispatch.unsubscribeUpdateOutputStreams()
      const _subscribe = function (key:string, variantId: string) : void {
        subscriptions.outputStreams[variantId] = fb.db
            .collection('streams')
            .doc(variantId)
            .onSnapshot(
              snap => {
                const data = snap.data() as Stream
                if (data) {
                  const stream = { data, id: key }
                  commit.setOutputStreamObject(stream)
                }
              },
              error => {
                console.error('error updateOutputStreams ' + variantId + ' ' + error)
              }
            )
      }

      const keys = ['rtsppreview', 'rtmppreview', 'rtsppreviewlow', 'rtmppreviewlow', 'rtsplowres', 'rtmplowres']
      for (const i in keys) {
        const key = keys[i]
        if (state.outputStreams.hasOwnProperty(key)) {
          const variantId = state.outputStreams[key].stream
          if (variantId === undefined) break
          _subscribe(key, variantId)
        }
      }
      const playlistVariant = `playlist${state.currentCast.id}`
      _subscribe('playlist', playlistVariant)


    }
  },
  unsubscribeCastTeam (_context, teamId) {
    if (teamId === undefined) {
      Object.keys(subscriptions.castTeams).forEach(teamId => {
        unsubscribeCastTeamId(teamId)
      })
    } else if (subscriptions.castTeams[teamId] !== undefined) {
      unsubscribeCastTeamId(teamId)
    }
  },
  async getSingleEvent (_context, eventId: string): Promise<Cast> {
    //Retrieves for *new* event definition, i.e., a cast
    try {
    //TODO: How to set this up to not hang Event Wizard page waiting for it?
    const result = await fb.db.collection(CollectionNames.CASTS).doc(eventId).get()
    return result.data() as Cast ?? new Cast()
  } catch (error) {
      console.log('Error retrieving Event object from fb', error)
      return new Cast()
    }
  },
  async getAllTeamAssetGroupIds (_context, teamId: string): Promise<string[]> {
    try {
      const result = await fb.db.collection(CollectionNames.ASSET_GROUPS)
      .where('team_id', '==', teamId)
      .where('deleted', '==', false)
      .get()

      return result.docs.map(doc => doc.id)
    } catch (error) {
      console.log('Error retrieving all of team Asset Groups from fb', error)
      return []
    }
  },
  async getAllTeamAssetGroups (_context, teamId: string): Promise<KeyDict<AssetGroup>> {
    try {
      const result = await fb.db.collection(CollectionNames.ASSET_GROUPS)
      .where('team_id', '==', teamId)
      .where('deleted', '==', false)
      .get()

      const groups: KeyDict<AssetGroup> = {}
      result.forEach(doc => {
        groups[doc.id] = {...doc.data(), id: doc.id } as AssetGroup
      })
      return groups
    } catch (error) {
      console.log('Error retrieving all of team Asset Groups from fb', error)
      return {}
    }
  },
  getCastTeam (context: EventsContext, teamId: string /*in reality: | undefined */) {
    const { commit, dispatch } = eventsModuleActionContext(context)
    console.log(`events.ts:getCastTeam(${teamId})`)
    return new Promise<void>(resolve => {
      commit.clearCastTeam()
      dispatch.unsubscribeCastTeam(teamId)
      subscriptions.castTeams[teamId] = { teams: NullUnsubscribe, profiles: {} }
      subscriptions.castTeams[teamId].teams = fb.db.collection('teams').doc(teamId).onSnapshot(snap => {
        const snapData = snap.data() as Team
        if (!snapData) return
        commit.updateCastTeamDoc(snapData)
        const members = snapData.members
        fb.db.collection('profiles').where('teams.' + teamId, '==', true).onSnapshot(snapMembers => {
          const memberDocs = snapMembers.docs
          for (let i = 0; i < memberDocs.length; i++) {
            const tmpMember = memberDocs[i].data() as UserProfile
            const member = members[tmpMember.id]
            if (member !== undefined && tmpMember !== undefined) {
              const tempRole = member.role !== undefined ? member.role : 'member'
              let displayName = ''
              let displayImage = ''
              if (tmpMember.hasOwnProperty('display_name')) {
                if (tmpMember.display_name !== '') {
                  displayName = tmpMember.display_name ?? ''
                }
              }
              if (tmpMember.hasOwnProperty('display_image')) {
                if (tmpMember.display_image !== '') {
                  displayImage = tmpMember.display_image ?? ''
                }
              }
              commit.loadCastMember({
                ...tmpMember,
                user_id: tmpMember.id,
                role: tempRole,
                first_name: tmpMember.first_name,
                last_name: tmpMember.last_name,
                display_name: displayName,
                display_image: displayImage
              })
            }
          }
        })
        resolve()
      }, error => {
        console.error('error getCastTeam ' + teamId + ' ' + error)
      })
    }).catch(error => {
      console.error(`Error getCastTeam 2 : teamId=${teamId} error=${error}`)
    })
  },
  updateLiveEditVolumes (context: EventsContext, payload: KeyDict<number>) {
    const { commit } = eventsModuleActionContext(context)
    commit.updateLiveEditVolumes(payload)
  },
  removeLiveEditVolumes (context: EventsContext, audioChannelIds: string[]) {
    const { commit } = eventsModuleActionContext(context)
    commit.removeLiveEditVolumes(audioChannelIds)
  },
  clearLiveEditVolumes (context: EventsContext) {
    const { commit } = eventsModuleActionContext(context)
    commit.clearLiveEditVolumes()
  },
  changeAudioMix (context: EventsContext, val: AudioMixType) {
    const { commit } = eventsModuleActionContext(context)
    commit.changeAudioMix(val)
  },
  updateVolumeOfStream (context: EventsContext, {id, val}: { id: string, val: KeyDict<number> }) {
    const { state } = eventsModuleActionContext(context)
    const currentCastId = state.currentCast.id
    const sourceStreamsCopy = {}
    sourceStreamsCopy[id] = {}
    Object.keys(val).forEach(volumeName => {
      if (['volume', 'volumeLeft', 'volumeRight', 'leftMuted', 'rightMuted'].includes(volumeName)) {
        sourceStreamsCopy[id][volumeName] = val[volumeName]
      }
    })
    fb.db
      .collection('casts')
      .doc(currentCastId)
      .set({ source_streams: sourceStreamsCopy }, { merge: true })
      .catch(error => {
        console.error('Update volume of Stream ', error)
      })
  },
  updateStreamVolumes (context: EventsContext, payload: AudioPayload[]) {
    const { commit } = eventsModuleActionContext(context)
    commit.updateStreamVolumes(payload)
  },
  applyMonitorToLive (context: EventsContext) {
    const { commit } = eventsModuleActionContext(context)
    commit.applyMonitorToLive()
  },
  changeGlobalTalkbackVolume (context: EventsContext, payload: number) {
    const { commit } = eventsModuleActionContext(context)
    commit.changeGlobalTalkbackVolume(payload)
  },
  changeTalkbackMode (context: EventsContext, payload: TalkbackMode) {
    const { commit } = eventsModuleActionContext(context)
    commit.changeTalkbackMode(payload)
  },
  toggleTalkbackGlobalMutes (context: EventsContext) {
    const { commit } = eventsModuleActionContext(context)
    commit.toggleTalkbackGlobalMutes()
  },
  saveSceneAsPreset (_context: EventsContext, { scene, team }: { scene: Scene, team: string }) {
    // remove the non-static assets from the scene
    for (const key in scene.contents) {
      if (scene.contents[key].type === 'caster' || scene.contents[key].type === 'broadcast') {
        delete scene.contents[key]
      }
    }
    const scenePresets = {}
    scenePresets[helpers.makeId()] = scene
    fb.db
      .collection('teams')
      .doc(team)
      .set({ scene_presets: scenePresets }, { merge: true })
      .catch(error => {
        console.error('Save Scene As Preset ', error)
      })
  },
  duplicateScene (context: EventsContext, scene: { scene: Scene }) {
    const { state } = eventsModuleActionContext(context)
    const currentCastId = state.currentCast.id
    const currentScenes: KeyDict<Scene> = cloneDeep(state.currentCast.scenes)
    const newSceneId = helpers.makeId()
    scene.scene.id = newSceneId
    scene.scene.name = scene.scene.name += ' copy'
    const sceneOrders = Object.values(currentScenes).map(scene => scene.order ?? 0)
    scene.scene.order = Math.max(...sceneOrders) + 1
    currentScenes[newSceneId] = scene.scene
    fb.db
      .collection('casts')
      .doc(currentCastId)
      .set({ scenes: currentScenes }, { merge: true })
      .catch(error => {
        console.error('Duplicate Scene ', error)
      })
  },
  setCefEvent (context: EventsContext) {
    const { commit } = eventsModuleActionContext(context)
    commit.setCefEvent()
  },
  setIosFront (context: EventsContext) {
    const { commit } = eventsModuleActionContext(context)
    commit.setIosFront()
  },
  setPreviewingOutput (context: EventsContext, value: boolean) {
    const { commit } = eventsModuleActionContext(context)
    commit.setPreviewingOutput(value)
  },
  setCefMode (context: EventsContext, mode: string) {
    const { commit } = eventsModuleActionContext(context)
    commit.setCefMode(mode)
  },
  setCefMixer (context: EventsContext, mixerid: string) {
    const { commit } = eventsModuleActionContext(context)
    commit.setCefMixer(mixerid)
  },
  bleepCaster (context: EventsContext, caster: String) {
    const { commit } = eventsModuleActionContext(context)
    commit.bleepCaster(caster)
  },
  toggleSimpleMode (context: EventsContext, value: boolean) {
    const { commit } = eventsModuleActionContext(context)
    commit.toggleSimpleMode(value)
  },
  toggleFullscreen (context: EventsContext, value: boolean) {
    const { commit } = eventsModuleActionContext(context)
    commit.toggleFullscreen(value)
  },
  updateCastSceneContent (_context: EventsContext,
                          status: { cast: string, scene: string, content: string, status: SceneContent }) {
    const data = { scenes: {} }
    data.scenes[status.scene] = { contents: {} }
    data.scenes[status.scene].contents[status.content] = status.status
    fb.db
      .collection('casts')
      .doc(status.cast)
      .set(data, { merge: true })
      .catch(error => {
        console.error('Update Cast Scene Content ', error)
      })
  },
  async setActiveScene (context: EventsContext, activeSceneId: string) {
    const { state } = eventsModuleActionContext(context)

    const activeScene = state.currentCast.scenes[activeSceneId]
    if (activeScene) {
      await fb.db.collection('casts').doc(state.currentCast.id).set({ activeScene: activeSceneId }, { merge: true })
    } else {
      throw new Error(`SetActiveScene: Scene with id ${activeSceneId} is not in the list of scenes`)
    }
  },
  async muteAssetInAllScenes (context: EventsContext, params : { assetId: string, mute: boolean , mvar? : MVar<void> }) {
    const { state } = eventsModuleActionContext(context)
    const update = {}
    const scenes = state.currentCast.scenes
    for (const sceneKey in scenes){
      const scene = scenes[sceneKey]
      const contentsDict = scene.contents ?? {}
      const contents = contentsDict[params.assetId]
      if (contents != undefined) {
        const path = `scenes.${sceneKey}.contents.${params.assetId}.muted` // better use FieldPath !
        update[path] = params.mute
      }
    }
    if (Object.keys(update).length == 0){
      return
    }
    try {
      await fb.db.collection('casts').doc(state.currentCast.id).update(update)
      if(params.mvar !== undefined) {
        params.mvar.put()
      }
    } catch(error) {
      console.error('muteAssetInAllScenes failed', error)
    }
  },
  async updateCastScene (context: EventsContext, scenes: DeepPartial<KeyDict<Scene>>) {
    const { state } = eventsModuleActionContext(context)
    await fb.db.collection('casts').doc(state.currentCast.id).set({scenes}, { merge: true })
  },
  updatePlaylistDoc (context: EventsContext,
                     clipstate: { position: number, team_id: string, active: string | null}) {
    const { state } = eventsModuleActionContext(context)
    fb.db
      .collection('playliststatus')
      .doc(state.currentCast.id)
      .set(clipstate, { merge: true })
      .catch(error => {
        console.error('Update playlist Doc error', error)
      })
  },
  toggleSelfMute (context: EventsContext, isSelfMuted: boolean) {
    const { commit, dispatch } = eventsModuleActionContext(context)
    commit.toggleSelfMute(isSelfMuted) //Update local state for immediate caster feedback
    dispatch.updateSelfMutedState({ //Store value update in db
      castId: state.currentCast.id,
      userId: store.getters.user.currentUserId,
      isSelfMuted: isSelfMuted
    })
  },
  async setPreviewScene (context: EventsContext, sceneData) {
    const { commit } = eventsModuleActionContext(context)
    commit.setPreviewScene(sceneData)
  },
  async updatePreviewScene (context: EventsContext, sceneData: Scene): Promise<void> {
    const { state } = eventsModuleActionContext(context)
    // FIXME: `sceneData` isn't always a real Scene object (see e.g. SingleScene::toggleItemLive())
    const layout = cloneDeep(sceneData.scene_layout)
    if (!sceneData.id || !layout) {
      throw new Error(`No Id in updateData (id = ${sceneData.id}, layout = ${layout})`)
    }
    const currentCastId = state.currentCast.id
    const oldPreviewScene: Scene|undefined = cloneDeep(state.currentCast.preview_scenes[sceneData.id])
    const oldSceneLayout: Layout|undefined = oldPreviewScene?.scene_layout
    const incompleteData = !!sceneData.contents && Object.values(sceneData.contents).some(o => !o.id)
    const changes = difference(sceneData, oldPreviewScene) as Partial<Scene>
    // console.warn('---------> CHANGES', changes)

    const noChanges = Object.keys(changes || {}).length === 0
    if (noChanges) {
      // console.warn('---------> XXX NO CHANGES', changes)
      return
    }

    if (incompleteData) {
      // When showing/hiding items in scene you only get a true/false
      // instead of a whole object  e.g. { aGc56uIP: true }
      // console.log('INCOMPLETE DATA')

      // get data from existing preview
      for (const key in sceneData.contents) {
        if (sceneData.contents) {
          sceneData.contents[key] = {
            ...(oldPreviewScene?.contents || {})[key],
            ...sceneData.contents[key]
          }
          // console.log('updated updateData.contents[key]: ', sceneData.contents[key])
        }
      }

      // also don't trust the layout data
      // @ts-ignore Should we even be assigning `{}` when oldPreviewScene has no `scene_layout`?
      sceneData.scene_layout = {
        ...(oldSceneLayout ?? {})
      }
      // console.log('updateData is now: ', sceneData)
    }

    const update = { preview_scenes: { [sceneData.id]: { scene_layout: layout, contents: {} } } }
    const hasLayoutChanges = changes.scene_layout !== undefined

    // ADD LAYOUT CONTENT
    let updatedContents = sceneData.contents || {}
    const layoutContents: KeyDict<SceneContent> =  sceneData.scene_layout?.contents ?? {}
    const layoutBoxes: KeyDict<LayoutBox> = sceneData.scene_layout?.boxes ?? {}

    // NEED TO UPDATE LAYOUT ?
    if (hasLayoutChanges) {
      // Add new layout items
      // make sure only one item is active per box
      Object.values(layoutContents).forEach((layoutItem: SceneContent) => {
        if (layoutItem.active) {
          const alreadyInContent = updatedContents.hasOwnProperty(layoutItem.id)
          const isOtherActive = (c: SceneContent) => c.active && c.id !== layoutItem.id && c.box === layoutItem.box
          const boxHasActiveContent = Object.values(updatedContents).some(isOtherActive)
          if (!alreadyInContent && boxHasActiveContent) {
            layoutItem.active = false
          }
        }
      })

      /*
        Add to scene all Simple CG assets newly included in layout.
        Accounts for any newly added Simple CG boxes, since they are otherwise only added to scenes when the scenes are first created in the cast.
        This partially recreates functionality already defined in SceneList.vue.
        We should possibly refactor to use a shared function instead.
      */
      Object.values(layoutBoxes).forEach((box: LayoutBox) => {
        //If it's a new SimpleCG box
        if (box.types?.includes(AssetType.SimpleCG) && !Object.keys(updatedContents).includes(box.id)) {
          //Set up some base property values
          let newActive = false
          let newSimplecgText = 'Sample Text'
          //Match new asset properties to an existing instance
          for (const existingScene of Object.values(state.currentCast.scenes)) {
            if (existingScene.contents?.[box.id]?.type === AssetType.SimpleCG) {
              //Update active flag and text content
              newActive = existingScene.contents[box.id].active ?? false
              newSimplecgText = existingScene.contents[box.id].simplecgText ?? 'Sample Text'
              break; //Stop loop early, only one value retrieval needed since they are supposed to be the same for all scenes anyway
            }
          }

          //Add new SimpleCG asset to scene
          updatedContents[box.id] = {
            id: box.id,
            name: box.name,
            order: 0,
            active: newActive,
            box: box.id,
            simplecgText: newSimplecgText,
            type: AssetType.SimpleCG
          }
        }
      })

      updatedContents = {
        ...layoutContents,
        ...updatedContents,
      }
    }

    update.preview_scenes[sceneData.id].contents = updatedContents
    update.preview_scenes[sceneData.id].scene_layout = sceneData.scene_layout
    await fb.db.collection(CollectionNames.CASTS).doc(currentCastId).set(update, { merge: true })
    // console.log('Firestore update ', update)

    // Cleanup assets that were deleted from layout
    if (hasLayoutChanges) {
      // console.log('oldPreviewScene.scene_layout.contents ', oldSceneLayout?.contents)
      // console.log('changes.scene_layout.contents ', changes.scene_layout?.contents)

      for (const key of Object.keys(oldSceneLayout?.contents ?? {})) {
        const wasRemoved = !!sceneData.scene_layout.contents && !sceneData.scene_layout.contents[key]
        if (wasRemoved) {
          // NOTE: we could also opt to never take away layout content automatically
          // this way the user has more control and will not see things dissappearing

          // set to true if you want to remove from preview content also
          const removeFromContent = true
          if (removeFromContent && sceneData.contents?.[key] === undefined) {
            // Only remove from the preview scene's contents if the non-preview scene also no longer contains it.
            const contentsPath = `preview_scenes.${sceneData.id}.contents.${key}`
            await fb.db
              .collection(CollectionNames.CASTS)
              .doc(currentCastId)
              .update({ [contentsPath]: fb.firebase.firestore.FieldValue.delete() })
          }

          // always remove from scene layout
          const layoutContentsPath = `preview_scenes.${sceneData.id}.scene_layout.contents.${key}`
          await fb.db
            .collection(CollectionNames.CASTS)
            .doc(currentCastId)
            .update({ [layoutContentsPath]: fb.firebase.firestore.FieldValue.delete() })
        }
      }
    }

    // check for deleted boxes
    for (const box of Object.values(oldSceneLayout?.boxes ?? {})) {
      if (box.id && !layout.boxes[box.id]) {
        const boxLocation = `preview_scenes.${sceneData.id}.scene_layout.boxes.${box.id}`
        await fb.db
          .collection(CollectionNames.CASTS)
          .doc(currentCastId)
          .update({ [boxLocation]: fb.firebase.firestore.FieldValue.delete() })
      }
    }
  },
  async applyChangesFromPreview (context: EventsContext) {
    const { state, commit } = eventsModuleActionContext(context)
    const currentCastId = state.currentCast.id
    const updateData = JSON.parse(JSON.stringify(state.currentCast.preview_scenes[state.sceneInPreview]))
    commit.leavePreviewScene()
    if (updateData.id && state.currentCast.scenes[updateData.id]) {
      const scene = state.currentCast.scenes[updateData.id]
      if (scene.scene_layout) {
        for (const box of Object.values(scene.scene_layout.boxes)) {
          if (box.id && updateData.scene_layout && !updateData.scene_layout.boxes[box.id]) {
            const boxlocation = 'scenes.' + updateData.id + '.scene_layout.boxes.' + box.id
            try {
              await fb.db
                .collection('casts')
                .doc(currentCastId)
                .update({
                  [boxlocation]: fb.firebase.firestore.FieldValue.delete()
                })
            } catch (error) {
              console.error('Update Scene from Preview - delete boxes', error)
            }
          }
        }
      }
    }
    if (updateData.id) {
      const currentCastScenes = JSON.parse(JSON.stringify(state.currentCast.scenes))
      updateData.active = currentCastScenes[updateData.id].active
      currentCastScenes[updateData.id] = updateData
      const sourceStreamsCopy = state.sourceStreams
      const changedData = {
        scenes: currentCastScenes,
        preview_scenes: {},
        source_streams: sourceStreamsCopy
      }
      try {
        await fb.db.collection('casts').doc(currentCastId).update({ scenes: changedData.scenes, preview_scenes: changedData.preview_scenes })
      } catch (error) {
        console.error('Update Scene from Preview - set data', error)
      }
    }
    // TODO set state also for quick response
  },
  clearPreviewScene (context: EventsContext) {
    const { commit } = eventsModuleActionContext(context)
    commit.clearPreviewScene()
  },
  async copyFromLiveToPreview (context: EventsContext, sceneId: string): Promise<void> {
    console.log('copyFromLiveToPreview')
    const { commit, state } = eventsModuleActionContext(context)
    const currentCastId = state.currentCast.id
    const currentSceneVer = state.currentCast.scenes[sceneId]
    const currentSceneCopy = cloneDeep(currentSceneVer)
    try {
      // console.log('Setting Firestore preview_scenes %s', sceneId)
      const fsPath = new fb.firebase.firestore.FieldPath('preview_scenes', sceneId)
      await fb.db.collection(CollectionNames.CASTS).doc(currentCastId).update(fsPath, currentSceneCopy)
    } catch (error) {
      console.error('Copy live to Preview ', error)
    }
    commit.setPreviewScene(currentSceneCopy)
  },
  addNewSceneToCast (context: EventsContext, scene: Scene) {
    const { state } = eventsModuleActionContext(context)
    if (scene.id === undefined) {
      console.error('events.addNewSceneToCast: scene.id is undefined')
      return
    }
    const currentScenes = JSON.parse(JSON.stringify(state.currentCast.scenes)) as KeyDict<Scene>
    // ADD LAYOUT CONTENT
    if (scene.scene_layout?.contents !== undefined) {
      scene.contents = {
        ...scene.scene_layout.contents,
        ...scene.contents
      }
      console.log('---> ADDED LAYOUT CONTENT TO SCENE', scene.contents)
    }
    const sceneUpdates = {}
    sceneUpdates[scene.id] = scene

    let videoOrder = maxOrderOfType(state.currentCast, AssetType.Video)
    if (scene.contents) {
      Object.entries(scene.contents).forEach(([contentId, content]) => {
        if (scene.contents === undefined) return
        if (content.type === AssetType.Video) {
          videoOrder++
          const newVideo = !isAssetInCast(state.currentCast, contentId)
          if (newVideo) scene.contents[contentId].order = videoOrder
          Object.keys(currentScenes).forEach(sceneId => {
            if (sceneUpdates[sceneId] === undefined) sceneUpdates[sceneId] = { contents: {}}
            sceneUpdates[sceneId].contents[contentId] = { ...content }
            const box = Object.entries(currentScenes[sceneId].scene_layout.boxes).find(([_boxid, box]) => {
              return box.types?.includes(AssetType.Video) === true
            })
            if (box !== undefined) sceneUpdates[sceneId].contents[contentId].box = box[0]
            if (newVideo) sceneUpdates[sceneId].contents[contentId].order = videoOrder
          })
        }
      })
    }

    const currentCastId = state.currentCast.id
    fb.db
      .collection('casts')
      .doc(currentCastId)
      .set({ scenes: sceneUpdates }, { merge: true })
      .catch(error => {
        console.error('Add Scene To Cast ', error)
      })
  },
  async deleteSceneFromCast ({ state }: EventsContext, scene: { id: string; name: string }) {
    const currentCastId = state.currentCast.id
    if (currentCastId === undefined) {
      throw Error('You are not in a cast, cannot delete scene')
    }
    const updates = { [`scenes.${scene.id}`]: fb.firebase.firestore.FieldValue.delete() }
    if (state.currentCast.preview_scenes[scene.id] !== undefined) {
      updates[`preview_scenes.${scene.id}`] = fb.firebase.firestore.FieldValue.delete()
    }
    await fb.db
      .collection(CollectionNames.CASTS)
      .doc(currentCastId)
      .update(updates)
    await store.dispatch.logs.addLog({
      message: 'Deleted scene from cast',
      cast: currentCastId,
      scene: scene.name,
      level: 'info'
    })
  },
  async deleteItemFromScene ({ state }: EventsContext, params: { all_scenes: boolean; scene: string; item: string, preview: boolean }) {
    const currentCastId = state.currentCast.id
    if (currentCastId === undefined) {
      throw Error('You are not in a cast, cannot delete scene item')
    }
    const deleteItems: KeyDict<any> = {}
    const prefix = params.preview ? 'preview_scenes' : 'scenes'
    if (params.all_scenes === true) {
      for (const sceneId of Object.keys(state.currentCast.scenes)) {
        deleteItems[`${prefix}.${sceneId}.contents.${params.item}`] = fb.firebase.firestore.FieldValue.delete()
      }
    } else {
      deleteItems[`${prefix}.${params.scene}.contents.${params.item}`] = fb.firebase.firestore.FieldValue.delete()
    }
    await fb.db.collection('casts').doc(currentCastId).update(deleteItems)
    await store.dispatch.logs.addLog({
      message: `Deleted item from scene`,
      cast: currentCastId,
      item: params.item,
      scene: params.scene,
      level: 'info'
    })
  },
  async setAutoPlayNextForVideo (context: EventsContext, params: { assetId: string, enabled: boolean }) {
    const { state } = eventsModuleActionContext(context)
    const currentCastId = state.currentCast.id
    if (currentCastId === undefined) {
      throw Error('You are not in a cast, cannot delete scene item')
    }16
    const castUpdate: DeepPartial<Cast> = {
      scenes: {}
    }
    for (const [sceneId, scene] of Object.entries(state.currentCast.scenes)) {
      if (scene.contents === undefined) return
      const asset: undefined|SceneContent = scene.contents[params.assetId]
      if (asset && castUpdate.scenes !== undefined) {
        castUpdate.scenes[sceneId] = {
          contents: {
            [params.assetId]: {
              auto_play_next: params.enabled
            }
          }
        }
      }
    }
    await fb.db.collection('casts').doc(currentCastId).set(castUpdate, { merge: true })
  },
  async setOrdersOfVideos (context: EventsContext, orders: KeyDict<number>) {
    const { state } = eventsModuleActionContext(context)
    const currentCastId = state.currentCast.id
    if (currentCastId === undefined) {
      throw Error('You are not in a cast, cannot delete scene item')
    }
    const castUpdate: DeepPartial<Cast> = { scenes: {} }
    for (const [sceneId, scene] of Object.entries(state.currentCast.scenes)) {
      if (scene.contents === undefined) return
      for (const [assetId, order] of Object.entries(orders)) {
        const asset: undefined|SceneContent = scene.contents[assetId]
        if (asset && castUpdate.scenes !== undefined) {
          if (castUpdate.scenes[sceneId] === undefined) castUpdate.scenes[sceneId] = { contents: {} }
          const scene = castUpdate.scenes[sceneId]
          if (scene?.contents !== undefined) scene.contents[assetId] = { order }
        }
      }
    }
    if (Object.keys(castUpdate.scenes!).length === 0) return
    await fb.db.collection('casts').doc(currentCastId).set(castUpdate, { merge: true })
  },
  async moveOrderOfAssets (context: EventsContext, params: {sceneId: string, fromContentId: string, toContentId: string}) {
    const { state } = eventsModuleActionContext(context)
    const assetInSyncBetweenScenes = cloneDeep(SocialMediaTypes)
    assetInSyncBetweenScenes.push(AssetType.Video)
    const scene = state.currentCast.scenes[params.sceneId]
    if (scene === undefined) throw Error('moveOrderOfAssets: Scene not found')
    if (scene.contents === undefined) return
    const fromContent = scene.contents[params.fromContentId]
    const toContent = scene.contents[params.toContentId]
    if (fromContent === undefined) throw Error('moveOrderOfAssets: fromContentId does not exists in the scene')
    if (toContent === undefined) throw Error('moveOrderOfAssets: toContentId does not exists in the scene')
    if (fromContent.box !== toContent.box) throw Error('moveOrderOfAssets: toContent and fromContent are not in the same box')

    const updateScene = (sceneId: string) => {
      const contents = state.currentCast.scenes[sceneId].contents
      if (contents === undefined) return {}
      const contentUpdates: KeyDict<{order: number, id: string}> = {}
      const direction = toContent.order > fromContent.order ? 0.5 : -0.5
      contentUpdates[params.fromContentId] = { order: toContent.order + direction, id: params.fromContentId }
      for (const [id, content] of Object.entries(contents)) {
        if (content.box === fromContent.box && id !== params.fromContentId) {
          contentUpdates[id] = { order: content.order, id }
        }
      }
      const updates = {
        scenes: {
          [sceneId]: {
            contents: {
            }
          }
        }
      }
      let order = 0
      const orderedContents = Object.values(contentUpdates).sort(contentSortByOrder)
      for (const content of orderedContents) {
        updates.scenes[sceneId].contents[content.id] = { order }
        order++
      }
      return updates
    }
    let updates: DeepPartial<Cast> = {}
    if (assetInSyncBetweenScenes.includes(fromContent.type)) {
      Object.keys(state.currentCast.scenes).forEach(sceneId => { updates = merge(updates, updateScene(sceneId))})
    } else {
      updates = updateScene(params.sceneId)
    }
    await fb.db.collection('casts').doc(state.currentCast.id).set(updates, { merge: true })
  },
  setCurrentCasterStream (context: EventsContext,
                          stream: SetCasterStreamInfo) {
    const { state, commit } = eventsModuleActionContext(context)
    const volume = store.state.user.profile?.caster_volume ?? 0
    const ss = 'source_streams.' + stream.streamId
    const currentCastId = state.currentCast.id
    fb.db
      .collection('casts')
      .doc(currentCastId)
      .update({
        [ss]: {
          type: stream.type,
          active: true,
          start_time: new Date().getTime() / 1000,
          volume,
          session_id: stream.sessionId,
          user_id: stream.userId
        }
      })
      .catch(error => {
        console.error('Set Current Caster Stream ', error)
      })
    commit.setCurrentCasterStream(stream)
  },
  setLocalCameraStream (context: EventsContext, stream: MediaStream) {
    const { commit } = eventsModuleActionContext(context)
    commit.setLocalCameraStream(stream)
  },
  setCasting (context: EventsContext, casting: boolean) {
    const { commit } = eventsModuleActionContext(context)
    commit.setCasting(casting)
  },
  killLocalCameraStream (context: EventsContext) {
    const { commit } = eventsModuleActionContext(context)
    commit.killLocalCameraStream()
  },
  async setOpenScene (context: EventsContext, scene: string) {
    const { commit } = eventsModuleActionContext(context)
    commit.setOpenScene(scene)
  },
  setSceneFilter (context: EventsContext, filter: string) {
    const { commit } = eventsModuleActionContext(context)
    commit.setSceneFilter(filter)
  },
  async deleteEvent (_context: EventsContext, eventId: string): Promise<void|any> {
    // TOOD: Maybe make this a transaction?
    try {
      const date = new Date()
      const eventDoc = await fb.db.collection(CollectionNames.EVENTS).doc(eventId).get()
      const event = eventDoc.data() as CloudCastEvent|undefined
      if (event === undefined) throw new Error(`Can't delete event '${eventId}' if it doesn't exist.`)
      await fb.db.collection(CollectionNames.EVENTS).doc(eventId)
                 .set({ deleted: true, end_date: helpers.getAtLeastStartDate(event, date) }, { merge: true })
      const casts = await fb.db.collection(CollectionNames.CASTS).where('event_id', '==', eventId).get()
      const promises: Promise<void>[] = []
      for (let i = 0; i < casts.docs.length; i++) {
        // TODO: Properly convert firebase data to Cast object.
        const cast = casts.docs[i].data() as Cast
        if (cast.end_date.seconds > date.getTime() / 1000) {
          promises.push(
            fb.db.collection(CollectionNames.CASTS).doc(casts.docs[i].id)
                 .set({ deleted: true, end_date: helpers.getAtLeastStartDate(cast, date) }, { merge: true })
                 .catch(error => { console.error('Delete event - list ', error) })
          )
        } else {
          promises.push(
            fb.db.collection(CollectionNames.CASTS).doc(casts.docs[i].id)
                 .set({ deleted: true }, { merge: true })
                 .catch(error => { console.error('Delete event - set delete true ', error) })
          )
        }
      }
      await Promise.all(promises)
      return
    } catch (e) {
      console.error(e)
      return e
    }
  },
  async stopEvent (_context: EventsContext, eventId: string): Promise<void|any> {
    // TOOD: Maybe make this a transaction?
    try {
      const date = new Date()
      const eventDoc = await fb.db.collection(CollectionNames.EVENTS).doc(eventId).get()
      const event = eventDoc.data() as CloudCastEvent|undefined
      if (event === undefined) throw new Error(`Can't stop event '${eventId}' if it doesn't exist.`)
      await fb.db.collection(CollectionNames.EVENTS).doc(eventId)
                 .set({ end_date: helpers.getAtLeastStartDate(event, date) }, { merge: true })
      const casts = await fb.db.collection(CollectionNames.CASTS).where('event_id', '==', eventId).get()
      const promises: Promise<void>[] = []
      for (let i = 0; i < casts.docs.length; i++) {
        // TODO: Properly convert firebase data to Cast object.
        const cast = casts.docs[i].data() as Cast
        if (cast.end_date?.seconds === undefined) console.warn('Cast has no or unexpected end_date property', cast)
        if (cast.end_date.seconds > date.getTime() / 1000) {
          promises.push(
            fb.db.collection(CollectionNames.CASTS).doc(casts.docs[i].id)
                 .set({ end_date: helpers.getAtLeastStartDate(cast, date) }, { merge: true })
          )
        }
      }
      await Promise.all(promises)

      return
    } catch (e) {
      console.error(e)
      return e
    }
  },
  setAudioInit (context: EventsContext, init: boolean): void {
    const { commit } = eventsModuleActionContext(context)
    commit.setAudioInit(init)
  },
  addStreamToCast (_context: EventsContext, stream: string) {
    // add the stream to the input_streams map
    const inputStreams: KeyDict<boolean> = cloneDeep(state.currentCast.input_streams)
    inputStreams[stream] = true

    const sourceStreams: KeyDict<Partial<SourceStream>> = cloneDeep(state.currentCast.source_streams)
    sourceStreams[stream] = {
      active: true,
      type: 'broadcast',
      start_time: new Date().getTime() / 1000,
      volume: 0,
      volumeLeft: 0,
      volumeRight: 0
    } as SourceStream
    // Now place it on the lowest order in each scene:

    const scenesContent = {} as SceneContents
    Object.values(state.currentCast.scenes).forEach(scene => {
      if (scene.id) {
        scenesContent[scene.id] = { contents: {} }
        if (scene.contents) {
          const broadcaststreams = Object.values(scene.contents).filter(it => {
            return it.type === AssetType.Broadcast
          })
          scenesContent[scene.id].contents[stream] = { order: broadcaststreams.length + 1 }
        }
      }
    })
    // TODO: in all commits: put async fb calls in actions, Mutations Must Be Synchronous
    // see https://vuex.vuejs.org/guide/mutations.html
    fb.db
      .collection('casts')
      .doc(state.currentCast.id)
      .set({ source_streams: sourceStreams, scenes: scenesContent, input_streams: inputStreams }, { merge: true })
      .catch(error => {
        console.error('Add Stream to Cast ', error)
      })
  },
  deleteStreamFromCast (context: EventsContext, stream: string) {
    const { state } = eventsModuleActionContext(context)
    const ss = 'source_streams.' + stream
    const is = 'input_streams.' + stream
    fb.db.collection('casts').doc(state.currentCast.id).update({
      [ss]: fb.firebase.firestore.FieldValue.delete(),
      [is]: fb.firebase.firestore.FieldValue.delete()
    })
    .catch(error => {
      console.error('Delete Stream from Cast ', error)
    })
  },
  updateMonitorVolume (context: EventsContext, volume: KeyDict<number>) {
    const { commit } = eventsModuleActionContext(context)
    commit.updateMonitorVolume(volume)
  },
  updateMonitorMutes (context: EventsContext, payload) {
    const { commit } = eventsModuleActionContext(context)
    commit.updateMonitorMutes(payload)
  },
  updateTalkbackVolumes (context: EventsContext, payload) {
    const { commit } = eventsModuleActionContext(context)
    commit.updateTalkbackVolumes(payload)
  },
  updateTalkbackMutes (context: EventsContext, payload) {
    const { commit } = eventsModuleActionContext(context)
    commit.updateTalkbackMutes(payload)
  },
  async removeCasterFromCast (context: EventsContext, castInfo: { casterId: string, castId: string, anonymousId?: string|undefined }) {
    try {
      const { state } = eventsModuleActionContext(context)
      const onlineSessions = state.currentCast.online_sessions
      if (castInfo.anonymousId !== undefined) {
        const sessionIdsToRemove = onlineSessions[castInfo.anonymousId]
        const sessions = onlineSessions[castInfo.casterId]
        if (sessions !== undefined && sessionIdsToRemove !== undefined) {
          onlineSessions[castInfo.casterId] = sessions.filter((sessionId) => !sessionIdsToRemove.includes(sessionId))
        }
        onlineSessions[castInfo.anonymousId] = []
      }

      const invitedCasters = state.currentCast.invited_casters
      invitedCasters[castInfo.casterId].deleted = true

      await fb.db.collection('casts').doc(castInfo.castId).update({
        online_sessions: onlineSessions,
        invited_casters: Convertable.toObject(invitedCasters)
      })
    } catch (error) {
      console.error('Remove Caster From Cast ', error)
    }
  },
  async addCasterToCast (context: EventsContext, castInfo: { castId: string, userId: string }) {
    const { state } = eventsModuleActionContext(context)
    try {
      const profile = state.castTeam.find((profile) => profile.id === castInfo.userId)
      if (profile !== undefined) {
        const invitedCasters = JSON.parse(JSON.stringify(state.currentCast.invited_casters))
        const data = invitedCasters[castInfo.userId] ? { ...invitedCasters[castInfo.userId] } : {}
        invitedCasters[castInfo.userId] = new InvitedCaster({ ...data, anonymous: !!profile.anonymous, userId: castInfo.userId, deleted: false })
        await fb.db.collection('casts').doc(castInfo.castId).set({ invited_casters: Convertable.toObject(invitedCasters) }, { merge: true })
        await store.dispatch.logs.addLog({
          message: 'User added as caster to cast.',
          cast: state.currentCast.id,
          data: { user: castInfo.userId, cast: castInfo.castId },
          level: 'info'
        })
      } else {
        await store.dispatch.logs.addLog({
          message: 'Failed to add caster to cast',
          cast: state.currentCast?.id,
          data: { error: `No profile found with id "${ castInfo.userId }".` },
          level: 'error'
        })
      }
    } catch (error) {
      await store.dispatch.logs.addLog({
        message: 'Failed to add caster to cast',
        cast: state.currentCast?.id,
        data: { error },
        level: 'error'
      })
    }
  },
  async updateInvitedCasters (context: EventsContext, castInfo: { castId: string, invitedCasters: KeyDict<InvitedCaster> }) {
    const { commit } = eventsModuleActionContext(context)
    commit.updateInvitedCasters(castInfo)
  },
  async setCasterTriage (context: EventsContext, payload: { userId: string, triage: CallTriage, moderatorId?: string }) {
    const { commit } = eventsModuleActionContext(context)
    commit.setCasterTriage(payload)
  },
  async addOutputToCast (context: EventsContext, payload) {
    const { commit } = eventsModuleActionContext(context)
    commit.addOutputToCast(payload)
  },
  async deleteCastOutput (context: EventsContext, payload) {
    const { commit } = eventsModuleActionContext(context)
    commit.deleteCastOutput(payload)
  },

  async toggleCastOutput (_context: EventsContext, payload: {castId: string, streamId: string, active: boolean}) {
    const currentCastId = payload.castId
    const streamId = payload.streamId
    const active = payload.active
    if (active  === true) {
      fb.db.collection('casts').doc(currentCastId).set(
        {
          custom_rtmp_destinations: { [streamId]: {'active': active, 'start_time': new Date().getTime() / 1000} }
        },
        { merge: true }
      )
      .catch(error=>{
        console.error('toggleCastOutput:', error)
      })
    }
    else {
      fb.db.collection('casts').doc(currentCastId).set(
        {
          custom_rtmp_destinations: { [streamId]: {'active':active}}
        },
        { merge: true }
      )
      .catch(error=>{
        console.error('toggleCastOutput:', error)
      })
    }
  },
  async toggleChat (context: EventsContext) {
    const { commit } = eventsModuleActionContext(context)
    commit.toggleChat()
  },
  async resetChatLastSeen (context: EventsContext) {
    const { commit } = eventsModuleActionContext(context)
    commit.resetLastSeen()
  },
  async setCastReady (context: EventsContext) {
    const { commit } = eventsModuleActionContext(context)
    commit.setCastReady()
  },
  async setActionPalleteTab (context: EventsContext, payload) {
    const { commit } = eventsModuleActionContext(context)
    commit.updateActionPalleteTab(payload)
  },
  setTalkbackStatus (context: EventsContext, talkback: boolean) {
    const { state } = eventsModuleActionContext(context)
    const currentCastId = state.currentCast.id
    const talkbackCopy: KeyDict<boolean> = cloneDeep(state.currentCast.talk_back)
    talkbackCopy[state.currentCasterStream] = talkback
    fb.db
      .collection('casts')
      .doc(currentCastId)
      .set({ talk_back: talkbackCopy }, { merge: true })
      .catch(error => {
        console.error('Set Talkback Status ', error)
      })
  },
  resetMonitorVolumes (context: EventsContext) {
    const { commit } = eventsModuleActionContext(context)
    commit.resetMonitorVolumes()
  },
  clearEvents (context: EventsContext) {
    const { commit } = eventsModuleActionContext(context)
    commit.clearEvents()
  },
  castEnded (context: EventsContext) {
    const { commit } = eventsModuleActionContext(context)
    commit.castHasEnded()
  },
  async makeUserModerator (_context: EventsContext, { user, cast, connected }: { user: string, cast: string, connected: boolean }) {
    try {
      let updates: DeepPartial<Cast> = {
        moderators: { [user]: { connected: true } }
      }
      if (!connected) {
        updates = {
          // @ts-ignore
          moderators: { [user]: firestore.FieldValue.delete() }
        }
      }
      await fb.db.collection(CollectionNames.CASTS).doc(cast).set(updates, { merge: true })
    } catch (error) {
      console.error('events.ts:makeUserModerator', error)
    }
  },
  async refreshCastToken (_context: EventsContext, cast: string) {
    try {
      const token = helpers.generateRandomString(24)
      await fb.db.collection(CollectionNames.CASTS).doc(cast).set({ token }, { merge: true })
    } catch (error) {
      console.error('events.ts:refreshCastToken', error)
    }
  },
  openActionPaletteInNewWindow ({state}) {
    const baseurl = window.location.origin
    const params = { id: state.currentCast.id, panel: 'actions' }
    window.open(baseurl + helpers.getURL(router, 'liveEventBreakout', params))
  },
  async sendMessage (_context: EventsContext, payload: { chat_id: string, msg: any}) {
    try {
      await fb.db.collection('chats').doc(payload.chat_id).collection('messages').add({
        ...payload.msg,
        timestamp: fb.serverTimestamp()
      })
    } catch (error) {
      console.error('send message onError ', error)
    }
  },
  updateSelfMutedState (_context: EventsContext, { castId, userId, isSelfMuted } : { castId: string, userId: string, isSelfMuted: boolean }) {
    if (castId && userId) {
      const payload = {
        self_muted: {
          [userId]: isSelfMuted
        }
      }
      fb.db.collection('casts').doc(castId).set(payload, { merge: true }) //Update caster's entry in dictionary
    }
    return
  },
  updateSimplecgText (context: EventsContext, payload: { simplecgId: string, newText: string, scenesIncluded: string[] }) {
    const { state } = eventsModuleActionContext(context)
    if (payload.simplecgId && payload.scenesIncluded?.length > 0 && state.currentCast?.scenes) {
      //Update text content of the asset across all scenes
      payload.scenesIncluded.forEach((scene) => {
        if (state.currentCast.scenes[scene]?.contents?.[payload.simplecgId]?.simplecgText) {
          fb.db.collection('casts').doc(state.currentCast.id).update({
            [`scenes.${scene}.contents.${payload.simplecgId}.simplecgText`]: payload.newText
          }) //Update caster's entry in dictionary in db
        }
      })
    }
  },
  setAutoPlayClips (context: EventsContext, autoPlay: { scene: string, state: boolean }) {
    const { state } = eventsModuleActionContext(context)
    if (typeof autoPlay.state === 'boolean' && state.currentCast.scenes[autoPlay.scene] !== undefined) {
      fb.db.collection('casts')
      .doc(state.currentCast.id)
      .set({
        scenes: {
          [autoPlay.scene]: {
            autoPlayClips: autoPlay.state
          }
        }
      }, { merge: true })
    }
  }
}

export const mutations = {
  updateCurrentPlaylist (state: EventsState, snap: firestore.DocumentSnapshot) {
    const doc = snap.data()
    if (doc) {
      Vue.set(state, 'currentPlaylist', doc)
    }
  },
  async updateDumpStateRequests (_state: EventsState, snap: firestore.DocumentSnapshot) {
    const doc = snap.data()
    console.log('updateDumpStateRequest doc=', JSON.stringify(doc))
    const castId = snap.id
    if (doc !== undefined){
      const now = new Date().getTime() //ms
      const keys = Object.keys(doc)
      const sortedKeys = keys.sort().reverse() // most recent first
      for( const key of sortedKeys){
        let keyNumber = 0
        try {
           keyNumber = parseFloat(key)
           if (keyNumber === NaN){
             keyNumber = 0
           }
        } catch(error){
          console.log(key, error)
        }
        const diff = now - keyNumber
        const LIMIT = 5000 // only requests entered less than LIMIT ms ago will be considered
        if (diff < LIMIT){
          if(!store.state.user.isDumping){
            const userState = store.state.user
            userState.isDumping = true
            const t0 = new Date().getTime()
            try{
              const presence = userState.presence
              let sessionId :string | undefined = presence?.sessionId
              if (sessionId === undefined && store.state.events.cefEvent){
                const cefMixer = store.state.events.cefMixer
                const cefMode = store.state.events.cefMode
                sessionId = `${cefMode}_${cefMixer}`
              }
              const dumps = doc[key]
              let downloadUrl : string | undefined = undefined
              const userId = userState.id
              const entryKey = `${userId}_${sessionId}`
              if (dumps !== undefined){
                downloadUrl = dumps[entryKey]
              }
              if (downloadUrl === undefined){
                const requestId = key
                downloadUrl = await logsModule.actions.dumpStateImpl(requestId)
                const update = { [key] :  { [entryKey] : downloadUrl }}
                await fb.db.collection('cast_dumps').doc(castId).set(update, {merge:true} )
                const t1 = new Date().getTime()
                const diff = t1 - t0
                console.log('dumping took:', diff)
                break
              }
            } finally{
              store.state.user.isDumping = false
            }
          }else {
            console.log('already dumping')
          }
        }
      }
      console.log('updateDumpRequests', JSON.stringify(doc))
    }
  },
  updateCurrentCast (_state: EventsState, snap: firestore.DocumentSnapshot) {
    const currentCast = snap.data()
    if (currentCast) {
      // console.log('events.ts mutations updateCurrentCast .team_id=', currentCast.team_id)
      store.commit.events.updateCurrentCastFromDoc({ id: snap.id, ...currentCast })
    }
  },
  updateCurrentCastFromDoc (state: EventsState, currentCast: any) {
    // FIXME: this function needs to be cleaned up
    const previousState = cloneDeep(state)

    if (state.cefMode === 'back') {
      const currentCastString = JSON.stringify(currentCast)
      localStorage.setItem('currentCast', currentCastString)
      // console.log('cast updated To Local state')
    }

    // console.log('currentCast from db', currentCast)
    // manually override undefined order coming from db

    for (const key in currentCast.scenes) {
      if (currentCast.scenes[key].name === undefined) {
        delete currentCast.scenes[key]
      } else {
        for (const i in currentCast.scenes[key].contents) {
          // console.log(currentCast.scenes[key].contents[i], Object.keys(currentCast.scenes[key].contents[i]))
          if (!currentCast.scenes[key].contents[i].hasOwnProperty('order')) {
            currentCast.scenes[key].contents[i].order = 10
          }
        }
        fillMissingCssObjects(currentCast.scenes[key].scene_layout, currentCast.id, currentCast.scenes[key].id) //Prevent errors on empty objects
      }

    }
    for (const key in currentCast.preview_scenes) {
      for (const i in currentCast.preview_scenes[key].contents) {
        // console.log(currentCast.scenes[key].contents[i], Object.keys(currentCast.scenes[key].contents[i]))
        if (!currentCast.preview_scenes[key].contents[i].hasOwnProperty('order')) {
          currentCast.preview_scenes[key].contents[i].order = 10
        }
      }
    }
    //
    lazyUpdate(state, 'currentCast', currentCast)
    // console.log('currentCast from state: ', state.currentCast)
    // console.log('sourceStreams equal:', isEqual(previousState.sourceStreams, currentCast.source_streams))
    if (!isEqual(previousState.sourceStreams, currentCast.source_streams)) {
      Vue.set(state, 'sourceStreams', currentCast.source_streams)
    }
    // console.log('outputStreams equal:', isEqual(previousState.outputStreams, currentCast.output_streams))
    if (!isEqual(previousState.outputStreams, currentCast.output_streams) && currentCast.output_streams !== undefined) {
      Vue.set(state, 'outputStreams', currentCast.output_streams)
    }
    // console.log('customStreams equal:', isEqual(previousState.customStreams, currentCast.custom_rtmp_destinations))
    if (!isEqual(previousState.customStreams, currentCast.custom_rtmp_destinations)) {
      Vue.set(state, 'customStreams', currentCast.custom_rtmp_destinations)
    }
    // not sure if we need to keep this. I believe it is old
    // if (!isEqual(previousState.talkBack, currentCast.talk_back)) {
    //   Vue.set(state, 'talkBack', currentCast.talk_back)
    // }

    const newProgramVolumes = {}
    const updateVolumeAttribute = (key: string, sourceAttributeName: string, targetKey: string) => {
      const sourceStream = state.sourceStreams[key]
      let newValue = sourceStream[sourceAttributeName]
      if (newValue === undefined || isNaN(newValue)){
        newValue = 0
      }
      newProgramVolumes[targetKey] = newValue
    }
    // we need to loop through source_streams and update their volumes from sourceStreams
    for (const key in state.sourceStreams) {
      updateVolumeAttribute(key, 'volume', key)
      updateVolumeAttribute(key, 'volumeLeft', key + '_left')
      updateVolumeAttribute(key, 'volumeRight', key + '_right')
    }
    if (!isEqual(state.programVolumes, newProgramVolumes)) {
      state.programVolumes = newProgramVolumes
    }
  },
  updateEvents (state: EventsState, snap: firestore.QuerySnapshot) {
    const events : CloudCastEvent[] = []
    snap.forEach(eventDoc => {
      const data = eventDoc.data()
      const event = {
        ...data,
        id : eventDoc.id
      } as CloudCastEvent
      events.push(event)
    })
    Vue.set(state, 'events', events)
    state.eventsLoaded = true
    if (firstEventsChecked) {
      state.eventsLoaded = true
    } else {
      firstEventsChecked = true
    }
  },
  updateCompletedEvents (state: EventsState, snap: firestore.QuerySnapshot) {
    const events: CloudCastEvent[] = []
    snap.forEach(eventDoc => {
      const data = eventDoc.data()
      const event = {
        ...data,
        id: eventDoc.id
      } as CloudCastEvent
      events.push(event)
    })
    Vue.set(state, 'completedEvents', events)
    setTimeout(() => {
      state.completedEventsLoaded = true
    }, 1000)
  },
  updateActiveCasts (state: EventsState, snap: firestore.QuerySnapshot) {
    state.activeCasts = {}
    snap.forEach(castDoc => {
      const data = castDoc.data() as CastSummary
      if (data.start_date < firestore.Timestamp.now()) {
        state.activeCasts[castDoc.id] = {
          name: data.name,
          id: castDoc.id,
          start_date: data.start_date
        }
      }
    })
  },
  updateMessages (state: EventsState, snap: firestore.QuerySnapshot) {
    // FIXME only update what is necessary
    const messages: CastChatMessage[] = []
    snap.forEach(message => {
      messages.push(message.data() as CastChatMessage)
    })
    state.eventMessages = messages
  },
  setEditCastLargeMachine (state: EventsState, largemachine: boolean) {
    state.createCastLargeMachine = largemachine
  },
  bleepCaster (state: EventsState, caster: String): void {
    const bleeped = {
      bleeped: {
      }
    }
    bleeped.bleeped[caster as string] = (new Date()).getTime()
    fb.db.collection('casts').doc(state.currentCast.id).set(bleeped, { merge: true }).catch(error => {
      console.error('bleepCaster Doc error', error)
    })
  },
  testModeSetCurrentCast (state: EventsState, value: Partial<Cast>): void {
    console.warn('You are using the function testModeSetCurrentCast, which should never run in production')
    // @ts-ignore
    state.currentCast = value
  },
  testModeSetCastTeam (state: EventsState, value: Partial<UserProfile>[]): void {
    console.warn('You are using the function testModeSetCastTeam, which should never run in production')
    // @ts-ignore
    state.castTeam = value
  },
  toggleSimpleMode (state: EventsState, value: boolean): void {
    state.simpleMode = value
  },
  toggleFullscreen (state: EventsState, value: boolean): void {
    state.fullscreen = value
  },
  setPreviewScene (state: EventsState, paramSceneData): void {
    state.sceneInPreview = paramSceneData.id
  },
  setStreamNames (state: EventsState, { teamStreams }: { teamStreams: KeyDict<Stream>}) {
    const names: KeyDict<string> = {}
    const images: KeyDict<string> = {}
    for (const key in state.sourceStreams) {
      const stream = state.sourceStreams[key]
      if (stream.type === 'broadcast' && stream.cast === undefined) {
        // get the name of the stream from where ever the team streams
        if (teamStreams.hasOwnProperty(key)) {
          if (teamStreams[key].name !== '') {
            names[key] = teamStreams[key].name
          } else {
            if (teamStreams[key].user !== undefined) {
              const person = state.castTeam.find(member => {
                return member.user_id === teamStreams[key].user
              })
              if (person !== undefined) {
                names[key] = person.first_name
              } else {
                names[key] = 'Input Stream'
              }
            } else {
              names[key] = 'Input Stream'
            }
          }
        } else {
          names[key] = 'Input Stream'
        }
      } else if (stream.type === 'caster') {
        const person = state.castTeam.find(member => {
          return member.user_id === stream.user_id
        })
        if (person) {
          if (person.display_name !== '' && person.display_name !== undefined) {
            names[key] = person.display_name
          } else {
            names[key] = `${person.first_name} ${person.last_name}`
          }
          if (person.display_image !== undefined && person.display_image !== '') {
            images[key] = person.display_image
          }
        }
      }
    }
    // lets get the caster names
    const casterNames = {}
    for (const key in state.currentCast.invited_casters) {
      const person = state.castTeam.find(member => {
        return member.user_id === key
      })
      if (person) {
        casterNames[key] = {}
        if (person.display_name !== '') {
          casterNames[key].name = person.display_name
        } else {
          casterNames[key].name = `${person.first_name} ${person.last_name}`
        }
        if (person.display_image !== undefined && person.display_image !== '') {
          casterNames[key].image = person.display_image
        }
      }
    }
    state.streamNames = names
    state.streamImages = images
    state.casterNames = casterNames
  },
  leavePreviewScene (state: EventsState) {
    state.sceneInPreview = ''
  },
  setCefMixer (state: EventsState, mixerid: string): void {
    state.cefMixer = mixerid
  },
  setCefEvent (state: EventsState) {
    state.cefEvent = true
  },
  setIosFront (state: EventsState) {
    state.iosFront = true
  },
  setPreviewingOutput (state: EventsState, value: boolean) {
    state.isPreviewingOutput = value
  },
  setCefMode (state: EventsState, mode: string) {
    state.cefMode = mode
  },
  clearPreviewScene (state: EventsState): void {
    state.sceneInPreview = ''
  },
  streamDocLoaded (state: EventsState, stream: { data: Stream, id: string }){
    const tmpStr = { id: stream.id, ...stream.data, streamDocLoaded: true }
    if ([StreamType.Broadcast, StreamType.Caster, StreamType.NonCaster, StreamType.Moderator].includes(tmpStr.type)) {
      Vue.set(state.streamDocs, stream.id, tmpStr)
    }
  },
  setOutputStreamObject (state: EventsState, stream: { data: Stream, id: string }) {
    if (stream.data.hasOwnProperty('castid')) {
      const tmpStr = { id: stream.id, ...stream.data, streamDocLoaded: true }
      Vue.set(state.streamDocs, stream.id, tmpStr)
    }
  },
  updateStreamVolumes (state: EventsState, updates: { key: string, value: number, name: string }[]) {
    // takes in structure
    // console.log('updateStreamVolumes', payload)
    const currentCastId = state.currentCast.id
    const sourceStreamsCopy = JSON.parse(JSON.stringify(state.sourceStreams))
    for (const update of updates) {
      const streamId = update.key
      const volumeName = update.name
      const volume = update.value
      console.log(streamId, volumeName, volume)
      if (['volume', 'volumeLeft', 'volumeRight', 'leftMuted', 'rightMuted'].includes(volumeName)) {
        //console.log('[', streamId,'][', volumeName,']:=',volume)
        sourceStreamsCopy[streamId][volumeName] = volume
      }
    }
    // console.log(sourceStreamsCopy)
    fb.db
      .collection('casts')
      .doc(currentCastId)
      .set({ source_streams: sourceStreamsCopy }, { merge: true })
      .catch(error => {
        console.error('updateStreamVolumes ', error)
      })
  },
  applyMonitorToLive (state: EventsState) {
    const volumeSource = JSON.parse(JSON.stringify(state.monitorVolumes))
    const muteSource = JSON.parse(JSON.stringify(state.monitorMutes))
    const currentCastId = state.currentCast.id
    const sourceStreamsCopy = JSON.parse(JSON.stringify(state.sourceStreams))
    console.log('applyMonitorToLive', volumeSource, muteSource)
    Object.keys(volumeSource).forEach(key => {
      const tempName = key.split('_')
      let volumeName = 'volume'
      let streamId = key
      const volume = volumeSource[key]
      if (tempName.length > 1) {
        volumeName += helpers.initialCaps(tempName[1])
        streamId = tempName[0]
      }
      // console.log(streamId, volumeName, volume)
      if (['volume', 'volumeLeft', 'volumeRight'].includes(volumeName)) {
        if (sourceStreamsCopy[streamId]) {
          sourceStreamsCopy[streamId][volumeName] = volume
        }
      }
    })
    // mutes are handled at the scene level so need to modify there
    const scenes = JSON.parse(JSON.stringify(state.currentCast.scenes))
    let activeScene = { id: '', contents: {} }
    if (state.currentCast.activeScene === null) {
      console.error('events.ts::applyMonitorToLive - state.currentCast.activeScene is null')
      return
    }
    activeScene = scenes[state.currentCast.activeScene]
    Object.keys(muteSource).forEach(key => {
      const tempName = key.split('_')
      let muteName = 'muted'
      let streamId = key
      const muteStatus = muteSource[key]
      if (tempName.length > 1) {
        muteName = tempName[1] + 'Muted'
        streamId = tempName[0]
      }
      console.log(streamId, muteName, muteStatus)
      if (['leftMuted', 'muted', 'rightMuted'].indexOf(muteName) !== -1) {
        activeScene.contents[streamId][muteName] = muteStatus
      }
    })
    scenes[activeScene.id] = activeScene
    fb.db
      .collection('casts')
      .doc(currentCastId)
      .set({ scenes: scenes, source_streams: sourceStreamsCopy }, { merge: true })
      .catch(error => {
        console.error('Update volume of Stream ', error)
      })
  },
  updateCastTeamDoc (state: EventsState, teamData: Team): void {
    state.castTeamDoc = teamData
  },
  updateInvitedCasters (_state: EventsState, castInfo: { castId: string, invitedCasters: KeyDict<InvitedCaster> }) {
    fb.db.collection('casts').doc(castInfo.castId).update({ invited_casters: Convertable.toObject(castInfo.invitedCasters) }).catch(error => {
      console.error('Add Caster To Cast ', error, castInfo.invitedCasters)
    })
  },
  setCasterTriage (state: EventsState, payload: { userId: string, triage: CallTriage, moderatorId?: string }) {
    const allCasters = state.currentCast.invited_casters
    const invitedCaster = allCasters[payload.userId]

    if (payload.triage === CallTriage.InModeration && payload.moderatorId) {
      invitedCaster.moderator = payload.moderatorId
    } else {
      invitedCaster.moderator = null
    }

    invitedCaster.triage = payload.triage
    invitedCaster.deleted = (payload.triage === CallTriage.Rejected)
    store.dispatch.events.updateInvitedCasters({ castId: state.currentCast.id, invitedCasters: allCasters })
  },
  addOutputToCast (state: EventsState, payload) {
    const currentCastId = state.currentCast.id
    let outputStreams = JSON.parse(JSON.stringify(state.currentCast.custom_rtmp_destinations))
    outputStreams = { ...outputStreams, ...payload }
    const ss = 'custom_rtmp_destinations'
    fb.db.collection('casts').doc(currentCastId).update({
      [ss]: outputStreams
    })
    .catch(error => {
      console.error('addOutputToCast:', error)
    })
  },
  deleteCastOutput (state: EventsState, payload) {
    const currentCastId = state.currentCast.id
    const outputStreams = JSON.parse(JSON.stringify(state.currentCast.custom_rtmp_destinations))
    if (outputStreams !== undefined) {
      delete outputStreams[payload]
      const ss = 'custom_rtmp_destinations'
      fb.db.collection('casts').doc(currentCastId).update({
        [ss]: outputStreams
      })
      .catch(error => {
        console.error('addOutputToCast:', error)
      })
    }
  },
  clearCurrentCast (state: EventsState): void {
    Vue.set(state, 'currentCast', {})
    state.actionPalleteTab = ActionPalleteTabs.Assets
    state.eventMessages = []
    state.openScene = ''
    state.sceneFilter = ''
    state.currentCasterStream = ''
    state.sourceStreams = {}
    state.outputStreams = {}
    state.streamDocs = {}
    state.sceneInPreview = ''
    state.simpleMode = true
    state.casting = false
    state.castingAs = ''
    state.eventsLoaded = false
    state.castEnded = false
    state.castReady = false
  },
  clearEvents (state: EventsState) {
    state.events = []
  },
  setCurrentCasterStream (state: EventsState, stream: { streamId: string, type: StreamType}) {
    state.currentCasterStream = stream.streamId
    state.castingAs = stream.type
  },
  updateMonitorVolume (state: EventsState, volumes: KeyDict<number>) {
    Vue.set(state, 'monitorVolumes', volumes)
  },
  resetMonitorVolumes (state: EventsState) {
    state.monitorVolumes = Object.assign({}, state.programVolumes)
  },
  setInCast (state: EventsState, flag: boolean) {
    state.inCast = flag
  },
  updateLiveEditVolumes (state: EventsState, payload: KeyDict<number>) {
    Object.keys(payload).forEach(audioChannelId => Vue.set(state.liveEditVolumes, audioChannelId, payload[audioChannelId]))
  },
  removeLiveEditVolumes (state: EventsState, audioChannelIds: string[]) {
    const tmpVolumes = { ...state.liveEditVolumes }
    audioChannelIds.forEach(audioChannelId => delete tmpVolumes[audioChannelId])
    state.liveEditVolumes = tmpVolumes
  },
  clearLiveEditVolumes (state: EventsState) {
    state.liveEditVolumes = {}
  },
  changeAudioMix (state: EventsState, val: AudioMixType) {
    if (val === AudioMixType.Monitor && Object.keys(state.monitorVolumes).length === 0) {
      state.monitorVolumes = Object.assign({}, state.programVolumes)
    }
    state.audioMix = val
  },
  setLocalCameraStream (state: EventsState, stream: MediaStream) {
    state.localCameraStream = stream
  },
  setCasting (state: EventsState, casting: boolean) {
    state.casting = casting
  },
  killLocalCameraStream (state: EventsState) {
    if (state.localCameraStream !== null) {
      if (state.localCameraStream.getAudioTracks().length > 0) {
        state.localCameraStream.getAudioTracks()[0].stop()
      }
      if (state.localCameraStream.getVideoTracks().length > 0) {
        state.localCameraStream.getVideoTracks()[0].stop()
      }
      state.localCameraStream = null
      if (state.currentCasterStream !== undefined && state.currentCasterStream !== '') {
        fb.db.collection('streams').doc(state.currentCasterStream).update({ 'active': false }).catch(error => {
          console.error('Kill Local Camera Stream 1 ', error)
        })
        const ss = 'source_streams.' + state.currentCasterStream
        const broadcastss = `${ss}broadcast`
        fb.db.collection('casts').doc(state.currentCast.id).update({
          [ss]: fb.firebase.firestore.FieldValue.delete(),
          [broadcastss]: fb.firebase.firestore.FieldValue.delete()
        }).catch(error => {
          console.error('Kill Local Camera Stream 2 ', error)
        })
      }
      //
      state.casting = false
      state.castingAs = ''
      state.currentCasterStream = ''
    }
  },
  setOpenScene (state: EventsState, scene: string) {
    if (state.openScene === scene) {
      state.openScene = ''
    } else {
      state.openScene = scene
    }
  },
  setSceneFilter (state: EventsState, filter: string) {
    state.sceneFilter = filter
  },
  toggleSelfMute (state: EventsState, status: boolean): void {
    state.selfMute = status
  },
  castHasEnded (state: EventsState) {
    state.castEnded = true
  },
  loadCastMember (state: EventsState, member: UserProfile) {
    let found = false
    for (let i = 0; i < state.castTeam.length; i++) {
      if (state.castTeam[i].user_id === member.user_id) {
        found = true
        Vue.set(state.castTeam, i, member)
      }
    }
    if (found === false) {
      state.castTeam.push(member)
    }
  },
  clearCastTeam (state: EventsState) {
    if (state.castTeam === undefined) return
    state.castTeam.splice(0, state.castTeam.length)
    // FIXME: should be havind null state as well
    state.castTeamDoc = {} as Team
  },
  updateMonitorMutes (state, payload) {
    Vue.set(state, 'monitorMutes', payload)
  },
  updateTalkbackVolumes (state, payload) {
    Vue.set(state, 'talkbackVolumes', {
      ...state.talkbackVolumes,
      ...payload
    })
  },
  updateTalkbackMutes (state, payload) {
    Vue.set(state, 'talkbackMutes', {
      ...state.talkbackMutes,
      ...payload
    })

    const someUnmuted = Object.values(payload || {}).some(muted => muted === false)
    const globalMuteOn = state.talkbackGlobalMute
    if (someUnmuted && globalMuteOn) {
      state.talkbackGlobalMute = false
    }
  },
  changeGlobalTalkbackVolume (state: EventsState, payload: number) {
    state.talkbackGlobalVolume = payload
  },
  changeTalkbackMode (state: EventsState, payload: TalkbackMode) {
    state.talkbackMode = payload
  },
  toggleTalkbackGlobalMutes (state: EventsState) {
    state.talkbackGlobalMute = !state.talkbackGlobalMute
    for (const key in state.talkbackMutes) {
      state.talkbackMutes[key] = state.talkbackGlobalMute
    }
  },
  toggleChat (state) {
    state.chatOpen = !state.chatOpen
    // remember when you last opened/closed the chat panel
    state.chatLastSeen = Date.now()
  },
  resetLastSeen (state) {
    state.chatLastSeen = Date.now()
  },
  setCastReady (state) {
    if (state.castReady) return
    state.castReady = true
  },
  updateActionPalleteTab (state, payload: ActionPalleteTabs) {
    state.actionPalleteTab = payload
  },
  setAudioInit (state: EventsState, init: boolean) {
    state.audioInit = init
  }
}

const eventsModule = {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
} as const

export default eventsModule
export const eventsModuleActionContext = context => moduleActionContext(context, eventsModule)
